// ─────────────────────────────────────────────────────────────────
// Control Systems — First & Second Order System Response
// ─────────────────────────────────────────────────────────────────

const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0 ? S.tdE : S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0 ? S.tdE : S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// Shared helpers
// ─────────────────────────────────────────────────────────────────
function mkAxisArrow(id, color) {
  return (
    <marker key={id} id={id} markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
      <polygon points="0 0,7 3,0 6" fill={color}/>
    </marker>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 1 — First-Order Step Response: RC circuit + computed curve
// ─────────────────────────────────────────────────────────────────
function DiagramFirstOrderStep() {
  const W = 900, H = 370;

  // Compute y(t) = 1 − e^(−t/tau) for three different tau values
  const taus   = [0.5, 1.0, 2.0];
  const colors = ['#c0392b', '#2E75B6', '#27ae60'];
  const tMax   = 7;
  const N      = 200;

  // Plot area
  const ox = 440, oy = 30, PW = 450, PH = 280;
  const sx = t => ox + 36 + (t / tMax) * (PW - 52);
  const sy = y => oy + PH - 24 - y * (PH - 44);

  // Build path data before JSX
  const curvePaths = taus.map(tau => {
    let d = '';
    for (let i = 0; i <= N; i++) {
      const t = i * tMax / N;
      const y = 1 - Math.exp(-t / tau);
      d += `${i === 0 ? 'M' : 'L'}${sx(t).toFixed(1)},${sy(y).toFixed(1)} `;
    }
    return d.trim();
  });

  // Tau marker positions (t = tau → y = 1−1/e ≈ 0.632)
  const tauMarkers = taus.map(tau => ({
    x: sx(tau),
    y: sy(0.632),
    tau,
  }));

  // Key time multiples for tau=1 annotations
  const tauRows = [
    { mult: 1, pct: '63.2', label: '1τ' },
    { mult: 2, pct: '86.5', label: '2τ' },
    { mult: 3, pct: '95.0', label: '3τ' },
    { mult: 4, pct: '98.2', label: '4τ' },
    { mult: 5, pct: '99.3', label: '5τ' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>First-Order System — Step Response: G(s) = K / (τs + 1)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          {mkAxisArrow('fo-blk', '#555')}
          {mkAxisArrow('fo-red', '#c0392b')}
        </defs>

        {/* ── LEFT: RC circuit schematic ── */}
        <rect x={10} y={10} width={415} height={350} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={212} y={28} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">RC Circuit — Canonical First-Order System</text>

        {/* Circuit wires */}
        <line x1={50}  y1={80}  x2={360} y2={80}  stroke="#333" strokeWidth="2"/>
        <line x1={50}  y1={210} x2={360} y2={210} stroke="#333" strokeWidth="2"/>
        <line x1={50}  y1={80}  x2={50}  y2={210} stroke="#333" strokeWidth="2"/>
        <line x1={360} y1={80}  x2={360} y2={210} stroke="#333" strokeWidth="2"/>

        {/* Voltage source */}
        <circle cx={50} cy={145} r={26} fill="#f0fff4" stroke="#27ae60" strokeWidth="2"/>
        <text x={50} y={141} textAnchor="middle" fontSize={14} fill="#27ae60" fontWeight="700">~</text>
        <text x={50} y={156} textAnchor="middle" fontSize={9}  fill="#27ae60">Vᵢₙ(s)</text>
        <text x={24} y={120} fontSize={10} fill="#27ae60" fontWeight="700">+</text>
        <text x={24} y={172} fontSize={10} fill="#27ae60" fontWeight="700">−</text>

        {/* Resistor R — top wire */}
        <rect x={100} y={68} width={80} height={24} rx={3} fill="#fff5e6" stroke="#e07b00" strokeWidth="2"/>
        <text x={140} y={84} textAnchor="middle" fontSize={12} fill="#e07b00" fontWeight="700">R</text>
        <text x={140} y={62} textAnchor="middle" fontSize={9}  fill="#e07b00">τ = RC</text>

        {/* Capacitor C — right branch */}
        <line x1={360} y1={80}  x2={360} y2={130} stroke="#333" strokeWidth="2"/>
        <line x1={332} y1={132} x2={388} y2={132} stroke="#9b59b6" strokeWidth="3.5"/>
        <line x1={332} y1={144} x2={388} y2={144} stroke="#9b59b6" strokeWidth="3.5"/>
        <line x1={360} y1={146} x2={360} y2={210} stroke="#333" strokeWidth="2"/>
        <text x={398} y={142} fontSize={13} fill="#9b59b6" fontWeight="700">C</text>

        {/* Output voltage label */}
        <text x={318} y={168} fontSize={11} fill="#c0392b" fontWeight="700">V_out(s)</text>
        <line x1={345} y1={145} x2={345} y2={175} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="3,2"/>
        <line x1={345} y1={175} x2={360} y2={175} stroke="#c0392b" strokeWidth="1.5"/>

        {/* Current arrow */}
        <line x1={200} y1={80} x2={240} y2={80} stroke="#c0392b" strokeWidth="2" markerEnd="url(#fo-red)"/>
        <text x={215} y={72} fontSize={10} fill="#c0392b" fontWeight="700">i(t)</text>

        {/* Transfer function derivation */}
        <rect x={18} y={230} width={396} height={122} rx={4} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={28} y={248} fontSize={11} fill="#7B0D0D" fontWeight="700">KVL + voltage divider:</text>
        <text x={28} y={265} fontSize={11} fill="#333" fontFamily="'Courier New',monospace">Vout/Vin = Zc/(R+Zc) = (1/Cs)/(R + 1/Cs)</text>
        <text x={28} y={283} fontSize={11} fill="#333" fontFamily="'Courier New',monospace">       = 1 / (RCs + 1) = 1 / (τs + 1)</text>
        <text x={28} y={303} fontSize={11} fill="#7B0D0D" fontWeight="700">Standard form  G(s) = K / (τs + 1):</text>
        <text x={28} y={320} fontSize={11} fill="#2E75B6" fontFamily="'Courier New',monospace">K = DC gain (steady-state output per unit input)</text>
        <text x={28} y={337} fontSize={11} fill="#e07b00" fontFamily="'Courier New',monospace">τ = time constant = RC  [seconds]</text>
        <text x={28} y={354} fontSize={10} fill="#555">Single pole at s = −1/τ  |  No zeros  |  Order = 1</text>

        {/* ── RIGHT: Step response curves ── */}
        <rect x={ox} y={oy} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={ox+PW/2} y={oy-8} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Step Response y(t) = K(1 − e^(−t/τ)) for Three τ Values</text>

        {/* Grid */}
        {[1,2,3,4,5,6,7].map(tv => (
          <g key={tv}>
            <line x1={sx(tv)} y1={oy+8} x2={sx(tv)} y2={oy+PH-22} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={sx(tv)} y={oy+PH-8} textAnchor="middle" fontSize={9} fill="#aaa">{tv}</text>
          </g>
        ))}
        {[0.2,0.4,0.6,0.8,1.0].map(yv => (
          <g key={yv}>
            <line x1={ox+34} y1={sy(yv)} x2={ox+PW-14} y2={sy(yv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={ox+30} y={sy(yv)+3} textAnchor="end" fontSize={9} fill="#aaa">{yv.toFixed(1)}</text>
          </g>
        ))}

        {/* Steady-state line */}
        <line x1={ox+34} y1={sy(1)} x2={ox+PW-14} y2={sy(1)} stroke="#27ae60" strokeWidth="1.2" strokeDasharray="6,4" opacity="0.7"/>
        <text x={ox+PW-10} y={sy(1)-4} fontSize={9} fill="#27ae60" fontWeight="700">K=1</text>

        {/* 63.2% line */}
        <line x1={ox+34} y1={sy(0.632)} x2={ox+PW-14} y2={sy(0.632)} stroke="#e07b00" strokeWidth="1" strokeDasharray="4,4" opacity="0.6"/>
        <text x={ox+36} y={sy(0.632)-4} fontSize={8.5} fill="#e07b00">63.2%</text>

        {/* Axes */}
        <line x1={ox+34} y1={oy+PH-22} x2={ox+PW-8}  y2={oy+PH-22} stroke="#aaa" strokeWidth="1.5" markerEnd="url(#fo-blk)"/>
        <line x1={ox+34} y1={oy+PH-22} x2={ox+34}    y2={oy+8}     stroke="#aaa" strokeWidth="1.5" markerEnd="url(#fo-blk)"/>
        <text x={ox+PW-4} y={oy+PH-18} fontSize={9} fill="#555">t</text>
        <text x={ox+38}   y={oy+6}      fontSize={9} fill="#555">y(t)</text>

        {/* Curves */}
        {curvePaths.map((d, i) => (
          <path key={i} d={d} fill="none" stroke={colors[i]} strokeWidth="2.2"/>
        ))}

        {/* Tau markers */}
        {tauMarkers.map(({ x, y, tau }, i) => (
          <g key={i}>
            <line x1={x} y1={oy+PH-22} x2={x} y2={y} stroke={colors[i]} strokeWidth="1.2" strokeDasharray="3,3" opacity="0.8"/>
            <line x1={ox+34} y1={y}    x2={x} y2={y} stroke={colors[i]} strokeWidth="1.2" strokeDasharray="3,3" opacity="0.8"/>
            <circle cx={x} cy={y} r={4} fill={colors[i]} stroke="#fff" strokeWidth="1.5"/>
            <text x={x+5} y={y-6} fontSize={9} fill={colors[i]} fontWeight="700">τ={tau}</text>
          </g>
        ))}

        {/* Legend */}
        <rect x={ox+36} y={oy+PH-20} width={PW-52} height={16} rx={2} fill="#fffcf5"/>
        {taus.map((tau, i) => (
          <g key={i}>
            <line x1={ox+44+i*130} y1={oy+PH-12} x2={ox+62+i*130} y2={oy+PH-12} stroke={colors[i]} strokeWidth="2.2"/>
            <text x={ox+65+i*130} y={oy+PH-8} fontSize={9} fill={colors[i]}>τ = {tau}s</text>
          </g>
        ))}

        {/* Tau multiples annotation table */}
        <rect x={ox+36} y={oy+PH+2} width={PW-52} height={64} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1"/>
        <text x={ox+PW/2} y={oy+PH+16} textAnchor="middle" fontSize={10} fill="#7B0D0D" fontWeight="700">Key time-constant milestones (K=1):</text>
        {tauRows.map(({ mult, pct, label }, i) => (
          <g key={i}>
            <rect x={ox+36+i*78} y={oy+PH+22} width={76} height={40} rx={2}
              fill={mult <= 2 ? '#fff5e6' : mult <= 4 ? '#eaf3ff' : '#f0fff4'}
              stroke={mult <= 2 ? '#e07b00' : mult <= 4 ? '#2E75B6' : '#27ae60'} strokeWidth="0.8"/>
            <text x={ox+74+i*78} y={oy+PH+37} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">{label}</text>
            <text x={ox+74+i*78} y={oy+PH+52} textAnchor="middle" fontSize={10} fill="#333">{pct}%</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The first-order step response y(t)=K(1−e^(−t/τ)) is a pure exponential rise. The time constant τ determines speed: at t=τ the output reaches 63.2% of its final value; at t=5τ it is within 0.7% — considered "settled". Larger τ means a slower, more sluggish response; smaller τ means faster. The DC gain K sets the final steady-state value.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 2 — First-Order Impulse & Ramp Responses
// ─────────────────────────────────────────────────────────────────
function DiagramFirstOrderInputTypes() {
  const W = 900, H = 320;

  // Computed curves for tau=1, K=1
  const tau = 1.0;
  const N   = 200;

  // Impulse: y(t) = (K/τ)e^(−t/τ) = e^(−t)
  const tMaxImp = 5;
  let impPath = '';
  for (let i = 0; i <= N; i++) {
    const t = i * tMaxImp / N;
    const y = (1 / tau) * Math.exp(-t / tau);
    impPath += `${i === 0 ? 'M' : 'L'}${(80 + 36 + (t / tMaxImp) * 240).toFixed(1)},${(60 + 200 - y * 180).toFixed(1)} `;
  }

  // Ramp: y(t) = K(t − τ(1−e^(−t/τ)))
  const tMaxRamp = 6;
  let rampNlPath = '', rampLinPath = '';
  for (let i = 0; i <= N; i++) {
    const t = i * tMaxRamp / N;
    const y = t - tau * (1 - Math.exp(-t / tau));
    const x = 430 + 36 + (t / tMaxRamp) * 240;
    const yp = 60 + 180 - y * 36;
    rampNlPath  += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${Math.max(62, yp).toFixed(1)} `;
    rampLinPath += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${(60 + 180 - t * 36).toFixed(1)} `;
  }

  // Frequency response (Bode-like, 1st order) — computed separately
  const wArr = [];
  for (let i = 0; i <= 100; i++) {
    const logw = -1.5 + i * 3.5 / 100;
    wArr.push(Math.pow(10, logw));
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>First-Order System — Impulse Response, Ramp Response &amp; Pole Location</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          {mkAxisArrow('fir-blk', '#aaa')}
          {mkAxisArrow('fir-red', '#c0392b')}
          {mkAxisArrow('fir-blue', '#2E75B6')}
        </defs>

        {/* ── PANEL 1: Impulse response ── */}
        <rect x={10} y={10} width={265} height={295} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={142} y={28} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Impulse Response</text>
        <text x={142} y={41} textAnchor="middle" fontSize={9}  fill="#555">y(t) = (K/τ)·e^(−t/τ)</text>

        {/* Axes */}
        <line x1={116} y1={260} x2={316} y2={260} stroke="#aaa" strokeWidth="1.5" markerEnd="url(#fir-blk)"/>
        <line x1={116} y1={260} x2={116} y2={58}  stroke="#aaa" strokeWidth="1.5" markerEnd="url(#fir-blk)"/>
        <text x={320} y={264} fontSize={9} fill="#aaa">t</text>
        <text x={120} y={55}  fontSize={9} fill="#aaa">y</text>

        {/* Grid lines */}
        {[1,2,3,4].map(tv => (
          <line key={tv} x1={116+tv*48} y1={58} x2={116+tv*48} y2={260} stroke="#f5f5f5" strokeWidth="1"/>
        ))}

        {/* Impulse curve */}
        <path d={impPath.trim()} fill="none" stroke="#c0392b" strokeWidth="2.5"/>

        {/* Annotations */}
        <circle cx={116} cy={60} r={5} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
        <text x={122} y={56} fontSize={9} fill="#c0392b" fontWeight="700">K/τ at t=0</text>
        <line x1={116+48} y1={260} x2={116+48} y2={60+180*Math.exp(-1)} stroke="#e07b00" strokeWidth="1.2" strokeDasharray="3,3"/>
        <text x={116+48} y={274} textAnchor="middle" fontSize={8.5} fill="#e07b00">τ</text>
        <circle cx={116+48} cy={Math.round(60+180*Math.exp(-1))} r={4} fill="#e07b00" stroke="#fff" strokeWidth="1.5"/>
        <text x={130} y={82} fontSize={8.5} fill="#e07b00">e⁻¹·(K/τ)</text>
        <text x={130} y={93} fontSize={8}   fill="#e07b00">≈ 0.368 K/τ</text>

        {/* Info box */}
        <rect x={16} y={268} width={257} height={30} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={144} y={281} textAnchor="middle" fontSize={9} fill="#7B0D0D" fontWeight="700">Laplace: Y(s) = K/(τs+1) · 1</text>
        <text x={144} y={293} textAnchor="middle" fontSize={9} fill="#555">Area under curve = K (steady-state gain check)</text>

        {/* ── PANEL 2: Ramp response ── */}
        <rect x={285} y={10} width={285} height={295} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={427} y={28} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Ramp Response</text>
        <text x={427} y={41} textAnchor="middle" fontSize={9}  fill="#555">y(t) = K[t − τ(1−e^(−t/τ))]</text>

        {/* Axes */}
        <line x1={466} y1={240} x2={690} y2={240} stroke="#aaa" strokeWidth="1.5" markerEnd="url(#fir-blk)"/>
        <line x1={466} y1={240} x2={466} y2={58}  stroke="#aaa" strokeWidth="1.5" markerEnd="url(#fir-blk)"/>
        <text x={694} y={244} fontSize={9} fill="#aaa">t</text>
        <text x={470} y={55}  fontSize={9} fill="#aaa">y</text>

        {/* Ideal ramp */}
        <path d={rampLinPath.trim()} fill="none" stroke="#aaa" strokeWidth="1.5" strokeDasharray="6,4"/>

        {/* Actual response */}
        <path d={rampNlPath.trim()} fill="none" stroke="#2E75B6" strokeWidth="2.5"/>

        {/* Steady-state error annotation */}
        <line x1={466+5*40} y1={240} x2={466+5*40} y2={240-5*36} stroke="#aaa" strokeWidth="1" strokeDasharray="2,2"/>
        <line x1={466+5*40} y1={240-(5-1)*36} x2={466+5*40} y2={240-5*36} stroke="#c0392b" strokeWidth="2"/>
        <text x={466+5*40+6} y={240-(5-0.5)*36} fontSize={9} fill="#c0392b" fontWeight="700">e_ss=Kτ</text>
        <text x={297} y={64} fontSize={9} fill="#2E75B6">Actual y(t)</text>
        <text x={297} y={76} fontSize={8.5} fill="#aaa">Ideal ramp (t)</text>

        <rect x={291} y={248} width={273} height={50} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={427} y={261} textAnchor="middle" fontSize={9} fill="#7B0D0D" fontWeight="700">Ramp: Y(s) = K/(τs+1) · 1/s²</text>
        <text x={427} y={274} textAnchor="middle" fontSize={9} fill="#555">Steady-state ramp error: e_ss = Kτ  (velocity error)</text>
        <text x={427} y={287} textAnchor="middle" fontSize={9} fill="#555">Response lags ramp by exactly τ seconds once settled</text>

        {/* ── PANEL 3: Pole-zero map & properties ── */}
        <rect x={580} y={10} width={310} height={295} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={735} y={28} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Pole Location &amp; Properties</text>

        {/* s-plane mini */}
        <rect x={588} y={35} width={170} height={150} rx={3} fill="#fafcff" stroke="#f0cece"/>
        {/* Stability shading */}
        <rect x={588} y={35} width={85} height={150} fill="#27ae60" opacity="0.06"/>
        <rect x={673} y={35} width={85} height={150} fill="#c0392b" opacity="0.06"/>
        {/* Axes */}
        <line x1={588} y1={110} x2={760} y2={110} stroke="#ccc" strokeWidth="1.2"/>
        <line x1={673} y1={35}  x2={673} y2={185} stroke="#ccc" strokeWidth="1.2"/>
        <text x={762} y={114} fontSize={8} fill="#aaa">σ</text>
        <text x={677} y={34}  fontSize={8} fill="#aaa">jω</text>

        {/* Pole at s = -1/tau */}
        <text x={645} y={115} textAnchor="middle" fontSize={20} fill="#c0392b" fontWeight="700">×</text>
        <text x={645} y={130} textAnchor="middle" fontSize={8.5} fill="#c0392b">s = −1/τ</text>
        <line x1={645} y1={110} x2={673} y2={110} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="3,2"/>
        <text x={658} y={105} textAnchor="middle" fontSize={8} fill="#e07b00">1/τ</text>

        {/* Labels */}
        <text x={625} y={48} fontSize={8.5} fill="#27ae60">Stable (LHP)</text>
        <text x={680} y={48} fontSize={8.5} fill="#c0392b">Unstable</text>

        {/* Properties list */}
        {[
          { y: 196, fill:'#fff5e6', bc:'#e07b00', lines:['Time constant τ', 'τ = −1/pole = 1/|σ|'] },
          { y: 228, fill:'#eaf3ff', bc:'#2E75B6', lines:['DC gain K', 'G(0) = K  (s→0 limit)'] },
          { y: 260, fill:'#f0fff4', bc:'#27ae60', lines:['Bandwidth ω_BW', '\u03c9_BW = 1/τ  rad/s'] },
          { y: 292, fill:'#FEF0F0', bc:'#c0392b', lines:['Rise time T_r', 'T_r \u2248 2.2\u03c4  (10%\u219990%)'] },
        ].map(({ y, fill, bc, lines }) => (
          <g key={y}>
            <rect x={586} y={y} width={296} height={28} rx={2} fill={fill} stroke={bc} strokeWidth="0.8"/>
            <text x={594} y={y+12} fontSize={9.5} fill="#7B0D0D" fontWeight="700">{lines[0]}</text>
            <text x={700} y={y+12} fontSize={9}   fill="#333" fontFamily="'Courier New',monospace">{lines[1]}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The impulse response of a first-order system is a decaying exponential starting at K/τ. The ramp response tracks the input ramp with a constant lag of exactly τ seconds once transients die out (steady-state ramp error = Kτ). The single real pole at s=−1/τ in the left half-plane guarantees stability; moving the pole further left (larger 1/τ) speeds up the response.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 3 — First-Order Bode Plot
// ─────────────────────────────────────────────────────────────────
function DiagramFirstOrderBode() {
  const W = 900, H = 360;

  // Compute Bode for G(jw) = K/(tau*jw+1), K=1, tau=1
  // Corner frequency = 1/tau = 1 rad/s
  const Npts = 150;
  const wMin = 0.05, wMax = 50;
  const tau = 1.0, K = 1.0;

  // Magnitude and phase arrays
  const magData = [];
  const phsData = [];
  for (let i = 0; i <= Npts; i++) {
    const w = wMin * Math.pow(wMax / wMin, i / Npts);
    const mag_dB = 20 * Math.log10(K / Math.sqrt(1 + (w * tau) ** 2));
    const phase_deg = -Math.atan(w * tau) * (180 / Math.PI);
    magData.push({ w, mag_dB });
    phsData.push({ w, phase_deg });
  }

  // Asymptote for magnitude
  const asymData = [];
  for (let i = 0; i <= Npts; i++) {
    const w = wMin * Math.pow(wMax / wMin, i / Npts);
    const mag_dB = w <= 1 / tau ? 0 : 20 * Math.log10(1 / (w * tau));
    asymData.push({ w, mag_dB });
  }

  // Plotting helpers
  const magOx = 40, magOy = 30, magW = 380, magH = 140;
  const phsOx = 40, phsOy = 195, phsW = 380, phsH = 140;

  const swx = w   => magOx + 36 + (Math.log10(w / wMin) / Math.log10(wMax / wMin)) * (magW - 52);
  const smag = dB => magOy + magH - 20 - ((dB + 40) / 60) * (magH - 30);
  const sphs = deg => phsOy + phsH - 20 - ((deg + 90) / 90) * (phsH - 30);

  const mkMagPath = data => data.map(({ w, mag_dB }, i) =>
    `${i === 0 ? 'M' : 'L'}${swx(w).toFixed(1)},${smag(Math.max(-42, mag_dB)).toFixed(1)}`).join(' ');
  const mkPhsPath = data => data.map(({ w, phase_deg }, i) =>
    `${i === 0 ? 'M' : 'L'}${swx(w).toFixed(1)},${sphs(phase_deg).toFixed(1)}`).join(' ');

  const magPath  = mkMagPath(magData);
  const asymPath = mkMagPath(asymData);
  const phsPath  = mkPhsPath(phsData);

  // Decade/frequency ticks
  const wTicks = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20];
  const magTicks = [20, 0, -20, -40];
  const phsTicks = [0, -30, -45, -60, -90];

  // Multiple taus comparison for right panels
  const tauVals   = [0.2, 0.5, 1.0, 2.0, 5.0];
  const tauColors = ['#c0392b','#e07b00','#2E75B6','#27ae60','#9b59b6'];

  const multiMag = tauVals.map(tv => {
    let d = '';
    for (let i = 0; i <= Npts; i++) {
      const w = wMin * Math.pow(wMax / wMin, i / Npts);
      const mag_dB = 20 * Math.log10(1 / Math.sqrt(1 + (w * tv) ** 2));
      const rx = 490 + 36 + (Math.log10(w / wMin) / Math.log10(wMax / wMin)) * 330;
      const ry = 30 + 160 - 20 - ((mag_dB + 40) / 60) * 120;
      d += `${i === 0 ? 'M' : 'L'}${rx.toFixed(1)},${Math.max(32, Math.min(ry, 168)).toFixed(1)} `;
    }
    return d.trim();
  });

  const multiPhs = tauVals.map(tv => {
    let d = '';
    for (let i = 0; i <= Npts; i++) {
      const w = wMin * Math.pow(wMax / wMin, i / Npts);
      const ph = -Math.atan(w * tv) * 180 / Math.PI;
      const rx = 490 + 36 + (Math.log10(w / wMin) / Math.log10(wMax / wMin)) * 330;
      const ry = 210 + 150 - 20 - ((ph + 90) / 90) * 120;
      d += `${i === 0 ? 'M' : 'L'}${rx.toFixed(1)},${Math.max(212, Math.min(ry, 348)).toFixed(1)} `;
    }
    return d.trim();
  });

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>First-Order System — Bode Plot: Magnitude &amp; Phase vs Frequency</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <clipPath id="bode1-clip-mag">
            <rect x={magOx+34} y={magOy+2} width={magW-48} height={magH-20}/>
          </clipPath>
          <clipPath id="bode1-clip-phs">
            <rect x={phsOx+34} y={phsOy+2} width={phsW-48} height={phsH-20}/>
          </clipPath>
          <clipPath id="bode1-clip-mmag">
            <rect x={528} y={32} width={364} height={148}/>
          </clipPath>
          <clipPath id="bode1-clip-mphs">
            <rect x={528} y={212} width={364} height={148}/>
          </clipPath>
        </defs>

        {/* ── LEFT: Single Bode ── */}
        {/* Magnitude plot */}
        <rect x={magOx} y={magOy} width={magW} height={magH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={magOx+magW/2} y={magOy-8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Magnitude  |G(jω)|  [dB]  — τ=1s, K=1</text>

        {/* Grid */}
        {wTicks.map(wv => (
          <g key={wv}>
            <line x1={swx(wv)} y1={magOy+4} x2={swx(wv)} y2={magOy+magH-18} stroke="#f0f0f0" strokeWidth="1"/>
            <text x={swx(wv)} y={magOy+magH-4} textAnchor="middle" fontSize={8} fill="#bbb">{wv}</text>
          </g>
        ))}
        {magTicks.map(mv => (
          <g key={mv}>
            <line x1={magOx+34} y1={smag(mv)} x2={magOx+magW-14} y2={smag(mv)} stroke="#f0f0f0" strokeWidth="1"/>
            <text x={magOx+30} y={smag(mv)+3} textAnchor="end" fontSize={8} fill="#bbb">{mv}</text>
          </g>
        ))}

        {/* Axes */}
        <line x1={magOx+34} y1={magOy+magH-18} x2={magOx+magW-10} y2={magOy+magH-18} stroke="#aaa" strokeWidth="1.2"/>
        <line x1={magOx+34} y1={magOy+6} x2={magOx+34} y2={magOy+magH-18} stroke="#aaa" strokeWidth="1.2"/>

        {/* Asymptote */}
        <path d={asymPath} fill="none" stroke="#aaa" strokeWidth="1.5" strokeDasharray="6,4" clipPath="url(#bode1-clip-mag)"/>

        {/* True curve */}
        <path d={magPath} fill="none" stroke="#c0392b" strokeWidth="2.5" clipPath="url(#bode1-clip-mag)"/>

        {/* Corner frequency annotation */}
        <line x1={swx(1)} y1={magOy+6} x2={swx(1)} y2={magOy+magH-18} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="4,3"/>
        <text x={swx(1)} y={magOy+magH+4} textAnchor="middle" fontSize={9} fill="#e07b00" fontWeight="700">ωc=1/τ=1</text>
        <circle cx={swx(1)} cy={smag(-3)} r={4} fill="#e07b00" stroke="#fff" strokeWidth="1.5"/>
        <text x={swx(1)+8} y={smag(-3)-4} fontSize={8.5} fill="#e07b00">−3 dB</text>

        {/* −20 dB/decade slope label */}
        <text x={swx(8)} y={smag(-18)} fontSize={9} fill="#aaa" fontStyle="italic">−20 dB/decade</text>

        {/* Phase plot */}
        <rect x={phsOx} y={phsOy} width={phsW} height={phsH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={phsOx+phsW/2} y={phsOy-8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Phase  ∠G(jω)  [°]</text>

        {wTicks.map(wv => (
          <g key={wv}>
            <line x1={swx(wv)} y1={phsOy+4} x2={swx(wv)} y2={phsOy+phsH-18} stroke="#f0f0f0" strokeWidth="1"/>
            <text x={swx(wv)} y={phsOy+phsH-4} textAnchor="middle" fontSize={8} fill="#bbb">{wv}</text>
          </g>
        ))}
        {phsTicks.map(pv => (
          <g key={pv}>
            <line x1={phsOx+34} y1={sphs(pv)} x2={phsOx+phsW-14} y2={sphs(pv)} stroke="#f0f0f0" strokeWidth="1"/>
            <text x={phsOx+30} y={sphs(pv)+3} textAnchor="end" fontSize={8} fill="#bbb">{pv}°</text>
          </g>
        ))}
        <line x1={phsOx+34} y1={phsOy+phsH-18} x2={phsOx+phsW-10} y2={phsOy+phsH-18} stroke="#aaa" strokeWidth="1.2"/>
        <line x1={phsOx+34} y1={phsOy+6}        x2={phsOx+34}      y2={phsOy+phsH-18} stroke="#aaa" strokeWidth="1.2"/>

        <path d={phsPath} fill="none" stroke="#2E75B6" strokeWidth="2.5" clipPath="url(#bode1-clip-phs)"/>

        {/* −45° at corner freq */}
        <line x1={swx(1)} y1={phsOy+4} x2={swx(1)} y2={phsOy+phsH-18} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="4,3"/>
        <circle cx={swx(1)} cy={sphs(-45)} r={4} fill="#e07b00" stroke="#fff" strokeWidth="1.5"/>
        <text x={swx(1)+6} y={sphs(-45)+4} fontSize={8.5} fill="#e07b00">−45°</text>
        <text x={phsOx+36} y={sphs(-5)}  fontSize={8.5} fill="#2E75B6">0° (low ω)</text>
        <text x={phsOx+36} y={sphs(-84)} fontSize={8.5} fill="#2E75B6">−90° (high ω)</text>

        <text x={phsOx+36} y={phsOy+phsH-4} fontSize={8} fill="#aaa">ω [rad/s]</text>

        {/* ── RIGHT: Multi-tau comparison ── */}
        <rect x={488} y={30} width={404} height={162} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={690} y={22} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Magnitude — Effect of Different τ Values</text>

        {wTicks.map(wv => (
          <line key={wv} x1={490+36+(Math.log10(wv/wMin)/Math.log10(wMax/wMin))*330} y1={34}
            x2={490+36+(Math.log10(wv/wMin)/Math.log10(wMax/wMin))*330} y2={180} stroke="#f5f5f5" strokeWidth="1"/>
        ))}
        {[-20,0].map(mv => (
          <line key={mv} x1={528} y1={30+160-20-((mv+40)/60)*120} x2={892}
            y2={30+160-20-((mv+40)/60)*120} stroke="#f5f5f5" strokeWidth="1"/>
        ))}
        <line x1={528} y1={180} x2={894} y2={180} stroke="#aaa" strokeWidth="1.2"/>
        <line x1={528} y1={34}  x2={528} y2={180} stroke="#aaa" strokeWidth="1.2"/>

        {multiMag.map((d, i) => (
          <path key={i} d={d} fill="none" stroke={tauColors[i]} strokeWidth="1.8" clipPath="url(#bode1-clip-mmag)"/>
        ))}

        {/* Legend */}
        {tauVals.map((tv, i) => (
          <g key={i}>
            <line x1={530+i*74} y1={187} x2={554+i*74} y2={187} stroke={tauColors[i]} strokeWidth="1.8"/>
            <text x={557+i*74} y={191} fontSize={8.5} fill={tauColors[i]}>τ={tv}</text>
          </g>
        ))}

        {/* Phase multi-tau */}
        <rect x={488} y={210} width={404} height={144} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={690} y={205} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Phase — Effect of Different τ Values</text>
        <line x1={528} y1={358} x2={894} y2={358} stroke="#aaa" strokeWidth="1.2"/>
        <line x1={528} y1={214} x2={528} y2={358} stroke="#aaa" strokeWidth="1.2"/>
        {phsTicks.map(pv => (
          <line key={pv} x1={528} y1={210+150-20-((pv+90)/90)*120} x2={892} y2={210+150-20-((pv+90)/90)*120} stroke="#f5f5f5" strokeWidth="1"/>
        ))}

        {multiPhs.map((d, i) => (
          <path key={i} d={d} fill="none" stroke={tauColors[i]} strokeWidth="1.8" clipPath="url(#bode1-clip-mphs)"/>
        ))}

        <text x={534} y={355} fontSize={8} fill="#aaa">ω [rad/s]</text>
      </svg>
      <div style={S.dCaption}>The first-order Bode plot has a flat magnitude of 20·log₁₀(K) dB at low frequencies, a −3 dB point at the corner frequency ωc=1/τ, and a −20 dB/decade roll-off above it. The phase runs from 0° at low ω to −90° at high ω, passing through exactly −45° at ωc. Larger τ shifts the corner frequency left (bandwidth narrows); smaller τ shifts it right (wider bandwidth, faster response).</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 4 — Second-Order Standard Form & Pole Locations
// ─────────────────────────────────────────────────────────────────
function DiagramSecondOrderPoles() {
  const W = 900, H = 380;

  // Pole locations for different zeta, wn=1
  const wn = 1.0;
  const cases = [
    { zeta: 0.0,  label: 'Undamped (ζ=0)',          color: '#9b59b6', sigma: 0,    wd: 1.0  },
    { zeta: 0.3,  label: 'Underdamped (ζ=0.3)',     color: '#c0392b', sigma: 0.3,  wd: Math.sqrt(1-0.09)  },
    { zeta: 0.707,label: 'ζ=0.707 (max flat)',      color: '#e07b00', sigma: 0.707,wd: Math.sqrt(1-0.5)   },
    { zeta: 1.0,  label: 'Critically damped (ζ=1)', color: '#27ae60', sigma: 1.0,  wd: 0    },
    { zeta: 1.5,  label: 'Overdamped (ζ=1.5)',      color: '#2E75B6', sigma: null, wd: null },
  ];

  // Overdamped poles: s = -zeta*wn ± wn*sqrt(zeta^2-1)
  const z15 = 1.5;
  const od1 = -z15 * wn + wn * Math.sqrt(z15 * z15 - 1);  // ≈ -0.268
  const od2 = -z15 * wn - wn * Math.sqrt(z15 * z15 - 1);  // ≈ -2.732

  // s-plane plot
  const cx = 290, cy = 190, sc = 100;
  const px = (sigma) => cx - sigma * sc;
  const py = (wd)    => cy - wd * sc;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Second-Order System — Standard Form, Pole Locations &amp; Damping Cases</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          {mkAxisArrow('so-blk', '#aaa')}
          <clipPath id="so-clip">
            <rect x={100} y={50} width={380} height={290}/>
          </clipPath>
        </defs>

        {/* ── LEFT: s-plane ── */}
        <rect x={10} y={10} width={430} height={360} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={225} y={28} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">Pole Locations for ωₙ=1, Varying ζ</text>

        {/* Stability region shading */}
        <rect x={100} y={50} width={cx-100} height={290} fill="#27ae60" opacity="0.04"/>
        <rect x={cx}  y={50} width={490-cx} height={290} fill="#c0392b" opacity="0.04"/>

        {/* Unit circle (locus) */}
        <circle cx={cx} cy={cy} r={sc} fill="none" stroke="#ddd" strokeWidth="1.2" strokeDasharray="5,4" clipPath="url(#so-clip)"/>

        {/* Grid */}
        {[-2,-1,0,1].map(sv => (
          <g key={sv}>
            <line x1={px(sv)} y1={50} x2={px(sv)} y2={340} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={px(sv)} y={350} textAnchor="middle" fontSize={8} fill="#ccc">{-sv}</text>
          </g>
        ))}
        {[-2,-1,0,1,2].map(jv => (
          <g key={jv}>
            <line x1={100} y1={py(jv)} x2={490} y2={py(jv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={96} y={py(jv)+3} textAnchor="end" fontSize={8} fill="#ccc">{jv > 0 ? `j${jv}` : jv === 0 ? '0' : `−j${Math.abs(jv)}`}</text>
          </g>
        ))}

        {/* Axes */}
        <line x1={100} y1={cy} x2={490} y2={cy} stroke="#bbb" strokeWidth="1.5" markerEnd="url(#so-blk)"/>
        <line x1={cx}  y1={50} x2={cx}  y2={345} stroke="#bbb" strokeWidth="1.5" markerEnd="url(#so-blk)"/>
        <text x={494} y={cy+4} fontSize={9} fill="#888">σ</text>
        <text x={cx+4} y={48}  fontSize={9} fill="#888">jω</text>

        {/* Zeta angle lines */}
        {[0.3, 0.5, 0.707].map(z => {
          const angle = Math.acos(z);
          const len = 95;
          return (
            <g key={z}>
              <line x1={cx} y1={cy}
                x2={cx - len * z} y2={cy - len * Math.sqrt(1 - z * z)}
                stroke="#f0f0f0" strokeWidth="1" strokeDasharray="3,3"/>
              <line x1={cx} y1={cy}
                x2={cx - len * z} y2={cy + len * Math.sqrt(1 - z * z)}
                stroke="#f0f0f0" strokeWidth="1" strokeDasharray="3,3"/>
            </g>
          );
        })}

        {/* Poles for each case */}
        {/* Undamped ζ=0: on jω axis */}
        <text x={cx} y={py(1)+4} textAnchor="middle" fontSize={18} fill="#9b59b6" fontWeight="700">×</text>
        <text x={cx} y={py(-1)+4} textAnchor="middle" fontSize={18} fill="#9b59b6" fontWeight="700">×</text>
        <text x={cx+6} y={py(1)-8} fontSize={8.5} fill="#9b59b6">±j1 (ζ=0)</text>

        {/* Underdamped ζ=0.3 */}
        {[1,-1].map((s,i) => (
          <g key={i}>
            <text x={px(0.3)} y={py(s*Math.sqrt(1-0.09))+4} textAnchor="middle" fontSize={18} fill="#c0392b" fontWeight="700">×</text>
          </g>
        ))}
        <text x={px(0.3)+8} y={py(Math.sqrt(0.91))-6} fontSize={8.5} fill="#c0392b">−0.3±j0.95</text>

        {/* ζ=0.707 */}
        {[1,-1].map((s,i) => (
          <text key={i} x={px(0.707)} y={py(s*Math.sqrt(0.5))+4} textAnchor="middle" fontSize={18} fill="#e07b00" fontWeight="700">×</text>
        ))}
        <text x={px(0.707)+8} y={py(Math.sqrt(0.5))-6} fontSize={8.5} fill="#e07b00">−0.707±j0.707</text>

        {/* Critical ζ=1 (double pole at -1) */}
        <text x={px(1)} y={cy+4} textAnchor="middle" fontSize={18} fill="#27ae60" fontWeight="700">×</text>
        <text x={px(1)-4} y={cy-10} textAnchor="end" fontSize={8.5} fill="#27ae60">−1 (double, ζ=1)</text>

        {/* Overdamped ζ=1.5 */}
        <text x={px(-od1)} y={cy+4} textAnchor="middle" fontSize={18} fill="#2E75B6" fontWeight="700">×</text>
        <text x={px(-od2)} y={cy+4} textAnchor="middle" fontSize={18} fill="#2E75B6" fontWeight="700">×</text>
        <text x={px(-od1)+4} y={cy-10} fontSize={8.5} fill="#2E75B6">−0.27</text>
        <text x={px(-od2)+4} y={cy-10} fontSize={8.5} fill="#2E75B6">−2.73</text>
        <text x={px((-od1-od2)/2)} y={cy+22} textAnchor="middle" fontSize={8} fill="#2E75B6">ζ=1.5 (both real)</text>

        {/* ωn radius annotation */}
        <path d={`M ${cx} ${cy-10} A 10,10 0 0 0 ${cx-8} ${cy-6}`} fill="none" stroke="#aaa" strokeWidth="1"/>
        <text x={cx+6} y={cy-14} fontSize={8} fill="#aaa">ωₙ=|s|</text>

        {/* ── RIGHT: Formula + cases table ── */}
        <rect x={450} y={10} width={440} height={360} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={670} y={28} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">Standard Form &amp; Damping Cases</text>

        {/* TF formula */}
        <rect x={458} y={36} width={424} height={52} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={670} y={54} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontFamily="'Courier New',monospace" fontWeight="700">G(s) = ωₙ²  /  (s² + 2ζωₙs + ωₙ²)</text>
        <text x={670} y={72} textAnchor="middle" fontSize={10} fill="#555">Poles: s = −ζωₙ ± ωₙ√(ζ²−1)   |   ωₙ = natural frequency   |   ζ = damping ratio</text>

        {/* Cases */}
        {[
          { zeta:'ζ = 0', name:'Undamped', color:'#9b59b6', bg:'#f5d0ff',
            poles:'s = ±jωₙ  (imaginary axis)',
            resp:'Pure sinusoidal oscillation at ωₙ\nNo decay — sustained forever' },
          { zeta:'0 < ζ < 1', name:'Underdamped', color:'#c0392b', bg:'#FEF0F0',
            poles:'s = −ζωₙ ± jωd   ωd=ωₙ√(1−ζ²)',
            resp:'Decaying oscillation at ωd\nOvershoot present — most common design case' },
          { zeta:'ζ = 1/√2', name:'ζ=0.707 (Butterworth)', color:'#e07b00', bg:'#fff5e6',
            poles:'s = −ωₙ/√2 ± jωₙ/√2  (45° angle)',
            resp:'Maximally flat frequency response\n~4.3% overshoot — good general choice' },
          { zeta:'ζ = 1', name:'Critically Damped', color:'#27ae60', bg:'#f0fff4',
            poles:'s = −ωₙ  (repeated real pole)',
            resp:'Fastest response without overshoot\nRare in practice — sensitive to exact ζ' },
          { zeta:'ζ > 1', name:'Overdamped', color:'#2E75B6', bg:'#eaf3ff',
            poles:'s = −ζωₙ ± ωₙ√(ζ²−1)  (two real)',
            resp:'No overshoot — two distinct real poles\nSlower than critical; dominant pole sets speed' },
        ].map(({ zeta, name, color, bg, poles, resp }, i) => (
          <g key={i}>
            <rect x={458} y={97+i*52} width={424} height={50} rx={3} fill={bg} stroke={color} strokeWidth="0.8"/>
            <rect x={458} y={97+i*52} width={90} height={50} rx={3} fill={color} opacity="0.15"/>
            <text x={503} y={115+i*52} textAnchor="middle" fontSize={9}   fill={color} fontWeight="700">{zeta}</text>
            <text x={503} y={130+i*52} textAnchor="middle" fontSize={9}   fill={color} fontWeight="700">{name}</text>
            <text x={556} y={113+i*52} fontSize={9.5} fill="#333" fontFamily="'Courier New',monospace">{poles}</text>
            {resp.split('\n').map((l, j) => (
              <text key={j} x={556} y={127+i*52+j*13} fontSize={9} fill="#555">{l}</text>
            ))}
          </g>
        ))}

        {/* Key relations box */}
        <rect x={458} y={360} width={424} height={0} rx={2}/>
      </svg>
      <div style={S.dCaption}>The second-order standard form G(s)=ωₙ²/(s²+2ζωₙs+ωₙ²) has two parameters: natural frequency ωₙ (sets speed) and damping ratio ζ (sets shape). As ζ decreases from overdamped toward zero, poles move from the real axis into the complex plane, sweeping an arc of radius ωₙ — the complex pole locus is a semicircle in the left half-plane.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 5 — Second-Order Step Response Family
// ─────────────────────────────────────────────────────────────────
function DiagramSecondOrderFamily() {
  const W = 900, H = 370;

  const zetaVals  = [0.1, 0.3, 0.5, 0.707, 1.0, 1.5, 2.0];
  const zColors   = ['#c0392b','#e07b00','#9b59b6','#2E75B6','#27ae60','#555','#aaa'];
  const wn = 1.0;
  const tMax = 14, N = 300;

  // Compute step response for each zeta
  const stepPaths = zetaVals.map(z => {
    let d = '';
    for (let i = 0; i <= N; i++) {
      const t = i * tMax / N;
      let y;
      if (z < 1) {
        const wd = wn * Math.sqrt(1 - z * z);
        const sigma = z * wn;
        y = 1 - Math.exp(-sigma * t) * (Math.cos(wd * t) + (sigma / wd) * Math.sin(wd * t));
      } else if (z === 1) {
        y = 1 - Math.exp(-wn * t) * (1 + wn * t);
      } else {
        const s1 = -z * wn + wn * Math.sqrt(z * z - 1);
        const s2 = -z * wn - wn * Math.sqrt(z * z - 1);
        y = 1 + (wn * wn / (s1 * (s1 - s2))) * Math.exp(s1 * t) + (wn * wn / (s2 * (s2 - s1))) * Math.exp(s2 * t);
      }
      d += `${i === 0 ? 'M' : 'L'}${(40 + 36 + (t / tMax) * 480).toFixed(1)},${(20 + 280 - 24 - Math.min(Math.max(y, -0.1), 2.2) * 220).toFixed(1)} `;
    }
    return d.trim();
  });

  const sx = t  => 40 + 36 + (t / tMax) * 480;
  const sy = yv => 20 + 280 - 24 - yv * 220;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Second-Order Step Response Family — G(s) = ωₙ² / (s² + 2ζωₙs + ωₙ²),  ωₙ=1</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <clipPath id="sosr-clip">
            <rect x={75} y={20} width={483} height={278}/>
          </clipPath>
          {mkAxisArrow('sosr-blk', '#aaa')}
        </defs>

        {/* Main plot */}
        <rect x={40} y={20} width={520} height={280} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>

        {/* Grid */}
        {[2,4,6,8,10,12,14].map(tv => (
          <g key={tv}>
            <line x1={sx(tv)} y1={22} x2={sx(tv)} y2={298} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={sx(tv)} y={312} textAnchor="middle" fontSize={9} fill="#aaa">{tv}</text>
          </g>
        ))}
        {[0.5, 1.0, 1.5, 2.0].map(yv => (
          <g key={yv}>
            <line x1={76} y1={sy(yv)} x2={558} y2={sy(yv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={72} y={sy(yv)+3} textAnchor="end" fontSize={9} fill="#aaa">{yv}</text>
          </g>
        ))}

        {/* Steady-state line */}
        <line x1={76} y1={sy(1)} x2={558} y2={sy(1)} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="7,5" opacity="0.6"/>
        <text x={562} y={sy(1)+4} fontSize={9} fill="#27ae60" fontWeight="700">y=1</text>

        {/* Zero line */}
        <line x1={76} y1={sy(0)} x2={558} y2={sy(0)} stroke="#ddd" strokeWidth="1"/>

        {/* Axes */}
        <line x1={76} y1={298} x2={562} y2={298} stroke="#aaa" strokeWidth="1.5" markerEnd="url(#sosr-blk)"/>
        <line x1={76} y1={298} x2={76}  y2={18}  stroke="#aaa" strokeWidth="1.5" markerEnd="url(#sosr-blk)"/>
        <text x={566} y={302} fontSize={9} fill="#aaa">t</text>
        <text x={80}  y={16}  fontSize={9} fill="#aaa">y(t)</text>
        <text x={248} y={322} textAnchor="middle" fontSize={9} fill="#555">Time  (ωₙt)</text>

        {/* Step response curves */}
        {stepPaths.map((d, i) => (
          <path key={i} d={d} fill="none" stroke={zColors[i]} strokeWidth={i < 2 ? 2.5 : i < 4 ? 2.0 : 1.7} clipPath="url(#sosr-clip)"/>
        ))}

        {/* ζ labels at right edge */}
        {zetaVals.map((z, i) => {
          const t = tMax - 0.3;
          let y;
          if (z < 1) {
            const wd = wn * Math.sqrt(1 - z * z);
            const sigma = z * wn;
            y = 1 - Math.exp(-sigma * t) * (Math.cos(wd * t) + (sigma / wd) * Math.sin(wd * t));
          } else {
            y = 1 - Math.exp(-wn * t) * (1 + wn * t) * (z === 1 ? 1 : 0) +
                (z === 1 ? 0 : 1); // approximate for overdamped
            if (z === 1) y = 1 - Math.exp(-wn * t) * (1 + wn * t);
            else {
              const s1 = -z * wn + wn * Math.sqrt(z * z - 1);
              const s2 = -z * wn - wn * Math.sqrt(z * z - 1);
              y = 1 + (wn * wn / (s1 * (s1 - s2))) * Math.exp(s1 * t) + (wn * wn / (s2 * (s2 - s1))) * Math.exp(s2 * t);
            }
          }
          return (
            <text key={i} x={562} y={Math.max(26, Math.min(296, sy(y) + 3))} fontSize={8.5} fill={zColors[i]} fontWeight="700">ζ={z}</text>
          );
        })}

        {/* RIGHT: Overshoot / speed trade-off panel */}
        <rect x={575} y={20} width={315} height={280} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={732} y={38} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">%OS vs ζ  —  Design Trade-offs</text>

        {/* OS vs zeta computed */}
        {(() => {
          const osData = [];
          for (let i = 0; i <= 50; i++) {
            const z = 0.01 + i * 0.98 / 50;
            const os = Math.exp(-Math.PI * z / Math.sqrt(1 - z * z)) * 100;
            osData.push({ z, os });
          }
          const osx = z  => 582 + 10 + (z / 1.0) * 295;
          const osy = os => 248 - (os / 100) * 180;
          const osPath = osData.map(({ z, os }, i) =>
            `${i === 0 ? 'M' : 'L'}${osx(z).toFixed(1)},${osy(os).toFixed(1)}`).join(' ');

          const osMarkers = [
            { z: 0.1,   os: Math.exp(-Math.PI * 0.1 / Math.sqrt(1 - 0.01)) * 100 },
            { z: 0.3,   os: Math.exp(-Math.PI * 0.3 / Math.sqrt(1 - 0.09)) * 100 },
            { z: 0.5,   os: Math.exp(-Math.PI * 0.5 / Math.sqrt(1 - 0.25)) * 100 },
            { z: 0.707, os: Math.exp(-Math.PI * 0.707 / Math.sqrt(1 - 0.5)) * 100 },
          ];

          return (
            <>
              {/* Grid */}
              {[0.2,0.4,0.6,0.8,1.0].map(z => (
                <g key={z}>
                  <line x1={osx(z)} y1={50} x2={osx(z)} y2={252} stroke="#f5f5f5" strokeWidth="1"/>
                  <text x={osx(z)} y={263} textAnchor="middle" fontSize={8} fill="#bbb">{z}</text>
                </g>
              ))}
              {[20,40,60,80,100].map(os => (
                <g key={os}>
                  <line x1={592} y1={osy(os)} x2={887} y2={osy(os)} stroke="#f5f5f5" strokeWidth="1"/>
                  <text x={588} y={osy(os)+3} textAnchor="end" fontSize={8} fill="#bbb">{os}%</text>
                </g>
              ))}

              {/* OS curve */}
              <path d={osPath} fill="none" stroke="#c0392b" strokeWidth="2" clipPath="url(#sosr-clip)"/>

              {/* Markers */}
              {osMarkers.map(({ z, os }, i) => (
                <g key={i}>
                  <line x1={osx(z)} y1={50} x2={osx(z)} y2={osy(os)} stroke={zColors[i]} strokeWidth="1" strokeDasharray="3,3"/>
                  <circle cx={osx(z)} cy={osy(os)} r={4} fill={zColors[i]} stroke="#fff" strokeWidth="1.5"/>
                  <text x={osx(z)+4} y={osy(os)-5} fontSize={8} fill={zColors[i]}>ζ={z}</text>
                  <text x={osx(z)+4} y={osy(os)+10} fontSize={8} fill={zColors[i]}>{os.toFixed(1)}%</text>
                </g>
              ))}

              {/* Axes */}
              <line x1={592} y1={252} x2={890} y2={252} stroke="#aaa" strokeWidth="1.2"/>
              <line x1={592} y1={50}  x2={592} y2={252} stroke="#aaa" strokeWidth="1.2"/>
              <text x={740} y={274} textAnchor="middle" fontSize={9} fill="#555">Damping ratio ζ</text>
              <text x={590} y={48}  fontSize={9} fill="#555" textAnchor="end">%OS</text>
            </>
          );
        })()}

        {/* Formula */}
        <rect x={580} y={275} width={305} height={22} rx={2} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={732} y={290} textAnchor="middle" fontSize={10} fill="#7B0D0D" fontFamily="'Courier New',monospace">%OS = e^(−πζ/√(1−ζ²)) × 100</text>

        {/* Bottom tick for t axis */}
        <text x={248} y={345} textAnchor="middle" fontSize={9} fill="#aaa">(time in units of 1/ωₙ seconds)</text>
      </svg>
      <div style={S.dCaption}>The second-order step response family shows a clear trade-off: lower ζ gives a faster initial rise but higher overshoot; higher ζ eliminates overshoot but slows the response. ζ=0.707 (Butterworth) is often chosen as it gives maximally flat magnitude response and only ~4.3% overshoot. The %OS vs ζ curve (right) is the designer's primary look-up tool: specify acceptable overshoot, read off required ζ.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 6 — Transient Performance Metrics (annotated response)
// ─────────────────────────────────────────────────────────────────
function DiagramTransientMetrics() {
  const W = 900, H = 380;

  // Underdamped step response, zeta=0.3, wn=2
  const zeta = 0.3, wn_m = 2.0;
  const wd   = wn_m * Math.sqrt(1 - zeta * zeta);
  const sig  = zeta * wn_m;
  const N    = 400, tMax = 7;

  const yFn = t => 1 - Math.exp(-sig * t) * (Math.cos(wd * t) + (sig / wd) * Math.sin(wd * t));

  const pts = [];
  for (let i = 0; i <= N; i++) {
    const t = i * tMax / N;
    pts.push({ t, y: yFn(t) });
  }

  const ox = 40, oy = 30, PW = 500, PH = 300;
  const sx = t  => ox + 36 + (t / tMax) * (PW - 52);
  const sy = yv => oy + PH - 24 - yv * (PH - 44);

  const pathD = pts.map(({ t, y }, i) =>
    `${i === 0 ? 'M' : 'L'}${sx(t).toFixed(1)},${sy(y).toFixed(1)}`).join(' ');

  // Key metric values
  const tPeak = Math.PI / wd;
  const yPeak = yFn(tPeak);
  const pctOS  = (yPeak - 1) * 100;

  // Rise time: 10% to 90%
  const t10Idx = pts.findIndex(p => p.y >= 0.1);
  const t90Idx = pts.findIndex(p => p.y >= 0.9);
  const t10 = pts[t10Idx]?.t ?? 0;
  const t90 = pts[t90Idx]?.t ?? 0;

  // Settling time (2% band)
  const tSettleIdx = pts.reduce((acc, p, i) => {
    if (i > 50 && pts.slice(i).every(q => Math.abs(q.y - 1) <= 0.02)) return i;
    return acc;
  }, N - 1);
  const tSettle = pts[tSettleIdx]?.t ?? tMax - 0.5;

  // Delay time (50%)
  const t50Idx = pts.findIndex(p => p.y >= 0.5);
  const t50    = pts[t50Idx]?.t ?? 0;

  // Metrics data
  const metrics = [
    { label: 'Delay time Td',     formula: '≈ (1+0.7ζ)/ωₙ',      value: t50.toFixed(2)+'s',    color: '#9b59b6' },
    { label: 'Rise time Tr',      formula: '(π−β)/ωd, β=cos⁻¹(ζ)', value: (t90-t10).toFixed(2)+'s', color: '#2E75B6' },
    { label: 'Peak time Tp',      formula: 'π/ωd',                  value: tPeak.toFixed(2)+'s',  color: '#e07b00' },
    { label: 'Overshoot %OS',     formula: 'e^(−πζ/√(1−ζ²))×100', value: pctOS.toFixed(1)+'%',  color: '#c0392b' },
    { label: 'Settling time Ts',  formula: '4/(ζωₙ)  [2% band]',   value: tSettle.toFixed(2)+'s', color: '#27ae60' },
    { label: 'Steady-state err',  formula: 'y(∞)−1  [unit step]',  value: '0.0 (Type ≥1)',        color: '#555' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Transient Performance Metrics — Annotated Step Response (ζ=0.3, ωₙ=2 rad/s)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          {mkAxisArrow('tm-blk', '#aaa')}
          {mkAxisArrow('tm-red', '#c0392b')}
          {mkAxisArrow('tm-blu', '#2E75B6')}
          {mkAxisArrow('tm-grn', '#27ae60')}
          {mkAxisArrow('tm-orn', '#e07b00')}
          {mkAxisArrow('tm-pur', '#9b59b6')}
          <clipPath id="tm-clip">
            <rect x={ox+34} y={oy} width={PW-48} height={PH-22}/>
          </clipPath>
        </defs>

        {/* Plot area */}
        <rect x={ox} y={oy} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={ox+PW/2} y={oy-8} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Step Response: y(t) — All Transient Metrics Annotated</text>

        {/* Grid */}
        {[1,2,3,4,5,6,7].map(tv => (
          <g key={tv}>
            <line x1={sx(tv)} y1={oy+4} x2={sx(tv)} y2={oy+PH-22} stroke="#f8f8f8" strokeWidth="1"/>
            <text x={sx(tv)} y={oy+PH-8} textAnchor="middle" fontSize={8.5} fill="#bbb">{tv}</text>
          </g>
        ))}
        {[0.5,1.0,1.5].map(yv => (
          <line key={yv} x1={ox+34} y1={sy(yv)} x2={ox+PW-14} y2={sy(yv)} stroke="#f5f5f5" strokeWidth="1"/>
        ))}

        {/* 2% band */}
        <rect x={ox+34} y={sy(1.02)} width={PW-48} height={sy(0.98)-sy(1.02)} fill="#27ae60" opacity="0.07"/>
        <line x1={ox+34} y1={sy(1.02)} x2={ox+PW-14} y2={sy(1.02)} stroke="#27ae60" strokeWidth="1" strokeDasharray="4,4" opacity="0.6"/>
        <line x1={ox+34} y1={sy(0.98)} x2={ox+PW-14} y2={sy(0.98)} stroke="#27ae60" strokeWidth="1" strokeDasharray="4,4" opacity="0.6"/>
        <text x={ox+36}  y={sy(1.02)-3} fontSize={8} fill="#27ae60">+2%</text>
        <text x={ox+36}  y={sy(0.98)+9} fontSize={8} fill="#27ae60">−2%</text>

        {/* Steady-state line */}
        <line x1={ox+34} y1={sy(1)} x2={ox+PW-14} y2={sy(1)} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="7,5" opacity="0.5"/>

        {/* Axes */}
        <line x1={ox+34} y1={oy+PH-22} x2={ox+PW-8} y2={oy+PH-22} stroke="#aaa" strokeWidth="1.5" markerEnd="url(#tm-blk)"/>
        <line x1={ox+34} y1={oy+PH-22} x2={ox+34}   y2={oy+4}     stroke="#aaa" strokeWidth="1.5" markerEnd="url(#tm-blk)"/>
        <text x={ox+PW-4} y={oy+PH-18} fontSize={9} fill="#aaa">t (s)</text>
        <text x={ox+38}   y={oy+4}      fontSize={9} fill="#aaa">y(t)</text>

        {/* Main curve */}
        <path d={pathD} fill="none" stroke="#1a1a2e" strokeWidth="2.5" clipPath="url(#tm-clip)"/>

        {/* ── Annotations ── */}
        {/* Delay time Td (50% mark) */}
        <line x1={sx(t50)} y1={sy(0)} x2={sx(t50)} y2={sy(0.5)} stroke="#9b59b6" strokeWidth="1.5" strokeDasharray="4,3"/>
        <line x1={ox+34}   y1={sy(0.5)} x2={sx(t50)} y2={sy(0.5)} stroke="#9b59b6" strokeWidth="1.5" strokeDasharray="4,3"/>
        <circle cx={sx(t50)} cy={sy(0.5)} r={4} fill="#9b59b6" stroke="#fff" strokeWidth="1.5"/>
        <text x={sx(t50)} y={oy+PH-8} textAnchor="middle" fontSize={8.5} fill="#9b59b6" fontWeight="700">Td</text>

        {/* Rise time: bracket from t10 to t90 */}
        <line x1={sx(t10)} y1={sy(0.1)} x2={sx(t10)} y2={oy+PH+14} stroke="#2E75B6" strokeWidth="1.2" strokeDasharray="3,2"/>
        <line x1={sx(t90)} y1={sy(0.9)} x2={sx(t90)} y2={oy+PH+14} stroke="#2E75B6" strokeWidth="1.2" strokeDasharray="3,2"/>
        <line x1={sx(t10)} y1={oy+PH+8} x2={sx(t90)} y2={oy+PH+8} stroke="#2E75B6" strokeWidth="2" markerStart="url(#tm-blu)" markerEnd="url(#tm-blu)"/>
        <text x={(sx(t10)+sx(t90))/2} y={oy+PH+22} textAnchor="middle" fontSize={8.5} fill="#2E75B6" fontWeight="700">Tr</text>
        <circle cx={sx(t10)} cy={sy(0.1)} r={3} fill="#2E75B6"/>
        <circle cx={sx(t90)} cy={sy(0.9)} r={3} fill="#2E75B6"/>

        {/* Peak time */}
        <line x1={sx(tPeak)} y1={sy(0)} x2={sx(tPeak)} y2={sy(yPeak)} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="4,3"/>
        <circle cx={sx(tPeak)} cy={sy(yPeak)} r={6} fill="#e07b00" stroke="#fff" strokeWidth="2"/>
        <text x={sx(tPeak)} y={oy+PH-8} textAnchor="middle" fontSize={8.5} fill="#e07b00" fontWeight="700">Tp</text>

        {/* Overshoot bracket */}
        <line x1={sx(tPeak)+18} y1={sy(1)} x2={sx(tPeak)+18} y2={sy(yPeak)} stroke="#c0392b" strokeWidth="1.8" markerStart="url(#tm-red)" markerEnd="url(#tm-red)"/>
        <text x={sx(tPeak)+22} y={(sy(1)+sy(yPeak))/2+4} fontSize={8.5} fill="#c0392b" fontWeight="700">%OS</text>
        <text x={sx(tPeak)+22} y={(sy(1)+sy(yPeak))/2+15} fontSize={8} fill="#c0392b">{pctOS.toFixed(1)}%</text>

        {/* Settling time */}
        <line x1={sx(tSettle)} y1={oy+4} x2={sx(tSettle)} y2={oy+PH-22} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="4,3"/>
        <text x={sx(tSettle)} y={oy+PH-8} textAnchor="middle" fontSize={8.5} fill="#27ae60" fontWeight="700">Ts</text>

        {/* 10% and 90% horizontal dashes */}
        <text x={ox+36} y={sy(0.1)+3}  fontSize={8} fill="#2E75B6">10%</text>
        <text x={ox+36} y={sy(0.9)+3}  fontSize={8} fill="#2E75B6">90%</text>
        <text x={ox+36} y={sy(0.5)+3}  fontSize={8} fill="#9b59b6">50%</text>
        <text x={ox+PW-10} y={sy(yPeak)+4} textAnchor="end" fontSize={8} fill="#e07b00">yₚ</text>

        {/* RIGHT: Metrics table */}
        <rect x={555} y={oy} width={335} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={722} y={oy+18} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Metric Formulas &amp; Values</text>
        <text x={722} y={oy+30} textAnchor="middle" fontSize={9}  fill="#555">(ζ=0.3, ωₙ=2 rad/s  →  ωd={wd.toFixed(3)} rad/s)</text>

        {metrics.map(({ label, formula, value, color }, i) => (
          <g key={i}>
            <rect x={563} y={oy+36+i*42} width={319} height={40} rx={3}
              fill={i % 2 === 0 ? '#fafafa' : '#fff'} stroke={color} strokeWidth="0.8"/>
            <rect x={563} y={oy+36+i*42} width={6} height={40} rx={1} fill={color}/>
            <text x={577} y={oy+50+i*42} fontSize={10}  fill={color} fontWeight="700">{label}</text>
            <text x={577} y={oy+63+i*42} fontSize={9}   fill="#555" fontFamily="'Courier New',monospace">{formula}</text>
            <text x={866} y={oy+57+i*42} textAnchor="end" fontSize={11} fill={color} fontWeight="700">{value}</text>
          </g>
        ))}

        {/* Approx formulas box */}
        <rect x={563} y={oy+PH-52} width={319} height={50} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={722} y={oy+PH-40} textAnchor="middle" fontSize={9} fill="#7B0D0D" fontWeight="700">Quick approximations (underdamped):</text>
        <text x={569} y={oy+PH-26} fontSize={9} fill="#333" fontFamily="'Courier New',monospace">%OS = e^(−πζ/√(1−ζ²))×100</text>
        <text x={569} y={oy+PH-13} fontSize={9} fill="#333" fontFamily="'Courier New',monospace">Ts ≈ 4/(ζωₙ)   |   Tp = π/ωd   |   Tr ≈ (π−cos⁻¹ζ)/ωd</text>
      </svg>
      <div style={S.dCaption}>All five standard transient metrics are shown on a single underdamped step response (ζ=0.3, ωₙ=2). Delay time Td (50% crossing), rise time Tr (10%→90%), peak time Tp (first peak), overshoot %OS (peak above unity), and settling time Ts (permanent entry into ±2% band). All formulas involve only ζ and ωₙ, making it straightforward to design for a specific set of specifications.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 7 — Second-Order Bode Plot Family
// ─────────────────────────────────────────────────────────────────
function DiagramSecondOrderBode() {
  const W = 900, H = 380;

  const zetaVals  = [0.1, 0.2, 0.3, 0.5, 0.707, 1.0, 2.0];
  const zColors   = ['#c0392b','#e07b00','#9b59b6','#2E75B6','#27ae60','#555','#aaa'];
  const Npts = 150;
  const wMin = 0.1, wMax = 10;

  // Magnitude & phase for each zeta
  const magPaths = zetaVals.map(z => {
    let d = '';
    for (let i = 0; i <= Npts; i++) {
      const w = wMin * Math.pow(wMax / wMin, i / Npts);
      const u = w;  // wn=1
      const mag_dB = 20 * Math.log10(1 / Math.sqrt((1 - u * u) ** 2 + (2 * z * u) ** 2));
      const x = 40 + 36 + (Math.log10(w / wMin) / Math.log10(wMax / wMin)) * 360;
      const y = 40 + 160 - 20 - ((Math.min(30, Math.max(-60, mag_dB)) + 60) / 90) * 130;
      d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)} `;
    }
    return d.trim();
  });

  const phsPaths = zetaVals.map(z => {
    let d = '';
    for (let i = 0; i <= Npts; i++) {
      const w = wMin * Math.pow(wMax / wMin, i / Npts);
      const u = w;
      const ph = -Math.atan2(2 * z * u, 1 - u * u) * 180 / Math.PI;
      const x = 40 + 36 + (Math.log10(w / wMin) / Math.log10(wMax / wMin)) * 360;
      const y = 220 + 160 - 20 - ((ph + 180) / 180) * 130;
      d += `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${Math.max(222, Math.min(y, 368)).toFixed(1)} `;
    }
    return d.trim();
  });

  const wTicks = [0.1, 0.2, 0.5, 1, 2, 5, 10];
  const swx = w => 40 + 36 + (Math.log10(w / wMin) / Math.log10(wMax / wMin)) * 360;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Second-Order System — Bode Plot Family for Varying ζ  (ωₙ = 1 rad/s)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <clipPath id="sob-mag">
            <rect x={74} y={42} width={364} height={152}/>
          </clipPath>
          <clipPath id="sob-phs">
            <rect x={74} y={222} width={364} height={152}/>
          </clipPath>
        </defs>

        {/* Magnitude panel */}
        <rect x={40} y={40} width={400} height={162} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={240} y={32} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Magnitude  |G(jω)|  [dB]</text>

        {wTicks.map(wv => (
          <g key={wv}>
            <line x1={swx(wv)} y1={44} x2={swx(wv)} y2={200} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={swx(wv)} y={210} textAnchor="middle" fontSize={8} fill="#bbb">{wv}</text>
          </g>
        ))}
        {[-60,-40,-20,0,20].map(mv => {
          const my = 40+160-20-((mv+60)/90)*130;
          return (
            <g key={mv}>
              <line x1={74} y1={my} x2={438} y2={my} stroke="#f5f5f5" strokeWidth="1"/>
              <text x={70} y={my+3} textAnchor="end" fontSize={8} fill="#bbb">{mv}</text>
            </g>
          );
        })}
        <line x1={74} y1={200} x2={440} y2={200} stroke="#aaa" strokeWidth="1.2"/>
        <line x1={74} y1={44}  x2={74}  y2={200} stroke="#aaa" strokeWidth="1.2"/>

        {/* Resonance peak annotation for low zeta */}
        <text x={swx(1)+4} y={60} fontSize={8.5} fill="#c0392b" fontStyle="italic">Resonance peak</text>
        <text x={swx(1)+4} y={71} fontSize={8.5} fill="#c0392b" fontStyle="italic">grows as ζ→0</text>

        {/* Corner frequency */}
        <line x1={swx(1)} y1={44} x2={swx(1)} y2={200} stroke="#e07b00" strokeWidth="1.2" strokeDasharray="4,3" opacity="0.7"/>
        <text x={swx(1)} y={206} textAnchor="middle" fontSize={8} fill="#e07b00" fontWeight="700">ωₙ</text>

        {magPaths.map((d, i) => (
          <path key={i} d={d} fill="none" stroke={zColors[i]} strokeWidth={i < 3 ? 2.2 : 1.8} clipPath="url(#sob-mag)"/>
        ))}

        {/* Phase panel */}
        <rect x={40} y={220} width={400} height={162} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={240} y={214} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Phase  ∠G(jω)  [°]</text>

        {wTicks.map(wv => (
          <g key={wv}>
            <line x1={swx(wv)} y1={224} x2={swx(wv)} y2={380} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={swx(wv)} y={390} textAnchor="middle" fontSize={8} fill="#bbb">{wv}</text>
          </g>
        ))}
        {[0,-45,-90,-135,-180].map(pv => {
          const py = 220+160-20-((pv+180)/180)*130;
          return (
            <g key={pv}>
              <line x1={74} y1={py} x2={438} y2={py} stroke="#f5f5f5" strokeWidth="1"/>
              <text x={70} y={py+3} textAnchor="end" fontSize={8} fill="#bbb">{pv}°</text>
            </g>
          );
        })}
        <line x1={74} y1={380} x2={440} y2={380} stroke="#aaa" strokeWidth="1.2"/>
        <line x1={74} y1={224} x2={74}  y2={380} stroke="#aaa" strokeWidth="1.2"/>

        <line x1={swx(1)} y1={224} x2={swx(1)} y2={380} stroke="#e07b00" strokeWidth="1.2" strokeDasharray="4,3" opacity="0.7"/>

        {phsPaths.map((d, i) => (
          <path key={i} d={d} fill="none" stroke={zColors[i]} strokeWidth={i < 3 ? 2.2 : 1.8} clipPath="url(#sob-phs)"/>
        ))}

        <text x={78} y={390} fontSize={8} fill="#aaa">ω [rad/s]</text>

        {/* −90° at ωn annotation */}
        <circle cx={swx(1)} cy={220+160-20-((-90+180)/180)*130} r={4} fill="#e07b00" stroke="#fff" strokeWidth="1.5"/>
        <text x={swx(1)+6} y={220+160-20-((-90+180)/180)*130+4} fontSize={8.5} fill="#e07b00">−90° at ωₙ (all ζ)</text>

        {/* RIGHT: Key features + legend */}
        <rect x={455} y={30} width={435} height={355} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={672} y={48} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Bode Plot Features &amp; Resonance</text>

        {/* Legend */}
        {zetaVals.map((z, i) => (
          <g key={i}>
            <rect x={463} y={56+i*28} width={290} height={26} rx={2}
              fill={i % 2 === 0 ? '#fafafa' : '#fff'} stroke={zColors[i]} strokeWidth="0.7"/>
            <line x1={470} y1={70+i*28} x2={498} y2={70+i*28} stroke={zColors[i]} strokeWidth={i < 3 ? 2.2 : 1.8}/>
            <text x={504} y={74+i*28} fontSize={9.5} fill={zColors[i]} fontWeight="700">ζ = {z}</text>
            <text x={560} y={74+i*28} fontSize={9}   fill="#555">
              {z < 0.5 ? `Resonance peak ≈ ${(1/(2*z*Math.sqrt(1-z*z))).toFixed(1)} (${(20*Math.log10(1/(2*z*Math.sqrt(1-z*z)))).toFixed(1)} dB)` :
               z === 0.5 ? 'Peak = 1.15 (1.2 dB)' :
               z === 0.707 ? 'Maximally flat (−3 dB at ωₙ)' :
               z === 1.0 ? 'No peak; −6 dB at ωₙ' : 'No peak; heavily damped'}
            </text>
          </g>
        ))}

        {/* Key features */}
        {[
          { fill:'#FEF0F0', bc:'#c0392b', y:258, title:'Resonant frequency ωᵣ',
            body:'ωᵣ = ωₙ√(1−2ζ²)   (exists only for ζ < 1/√2 ≈ 0.707)\nAt ζ=0.707: ωᵣ=0 — resonance just disappears' },
          { fill:'#eaf3ff', bc:'#2E75B6', y:308, title:'Resonant peak Mᵣ',
            body:'Mᵣ = 1/(2ζ√(1−ζ²))   [ratio, not dB]\nDominates frequency response for small ζ' },
          { fill:'#f0fff4', bc:'#27ae60', y:358, title:'Phase characteristic',
            body:'All curves pass through −90° exactly at ω=ωₙ\nSteep phase transition for low ζ (near −90°/decade)\nHigh ζ: gradual, benign phase shift' },
        ].map(({ fill, bc, y, title, body }) => (
          <g key={y}>
            <rect x={463} y={y} width={419} height={46} rx={3} fill={fill} stroke={bc} strokeWidth="1"/>
            <text x={471} y={y+14} fontSize={10}  fill="#7B0D0D" fontWeight="700">{title}</text>
            {body.split('\n').map((l, j) => (
              <text key={j} x={471} y={y+26+j*13} fontSize={9} fill="#333" fontFamily="'Courier New',monospace">{l}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The second-order Bode magnitude plot shows a −40 dB/decade roll-off at high frequencies. For ζ &lt; 0.707, a resonant peak appears at ωᵣ=ωₙ√(1−2ζ²), growing dramatically as ζ→0. The phase always passes through exactly −90° at ω=ωₙ regardless of ζ, and transitions from 0° to −180°. The sharpness of the phase transition increases as ζ decreases.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 8 — Pole Location → Time Domain Behaviour Map
// ─────────────────────────────────────────────────────────────────
function DiagramPoleMap() {
  const W = 900, H = 400;

  // Mini time-domain sketches inside the s-plane
  // For each pole type, draw a small inset curve

  const mkDecayOsc = (ox, oy, color, zeta) => {
    const pts = [];
    for (let i = 0; i <= 60; i++) {
      const t = i * 5 / 60;
      const y = Math.exp(-zeta * t) * Math.cos(Math.sqrt(1 - zeta * zeta) * t * 3);
      pts.push([ox + i * 1.4, oy - y * 20]);
    }
    return pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  };

  const mkDecay = (ox, oy, color) => {
    const pts = [];
    for (let i = 0; i <= 60; i++) {
      const t = i * 5 / 60;
      const y = Math.exp(-t * 1.2);
      pts.push([ox + i * 1.4, oy - y * 30]);
    }
    return pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  };

  const mkGrow = (ox, oy) => {
    const pts = [];
    for (let i = 0; i <= 40; i++) {
      const t = i * 3 / 40;
      const y = Math.exp(t * 1.5);
      pts.push([ox + i * 2.1, oy - Math.min(y * 8, 50)]);
    }
    return pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  };

  const mkSustain = (ox, oy) => {
    const pts = [];
    for (let i = 0; i <= 60; i++) {
      const t = i * 5 / 60;
      const y = Math.cos(t * 4);
      pts.push([ox + i * 1.4, oy - y * 22]);
    }
    return pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  };

  const mkGrowOsc = (ox, oy) => {
    const pts = [];
    for (let i = 0; i <= 50; i++) {
      const t = i * 4 / 50;
      const y = Math.exp(0.4 * t) * Math.cos(3 * t);
      pts.push([ox + i * 1.7, oy - Math.max(-45, Math.min(y * 15, 45))]);
    }
    return pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  };

  // Pre-compute all paths before JSX
  const decayOscPath05 = mkDecayOsc(592, 175, '#c0392b', 0.15);
  const decayOscPath03 = mkDecayOsc(592, 270, '#e07b00', 0.35);
  const decayPath      = mkDecay(592, 340, '#27ae60');
  const growPath       = mkGrow(710, 175, '#c0392b');
  const sustainPath    = mkSustain(710, 270, '#9b59b6');
  const growOscPath    = mkGrowOsc(710, 320, '#c0392b');

  // s-plane
  const cx = 340, cy = 200, sc = 80;
  const px = s => cx + s * sc;
  const py = w => cy - w * sc;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Pole Location → Time-Domain Behaviour Map</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          {mkAxisArrow('pm-blk', '#aaa')}
          <clipPath id="pm-splane">
            <rect x={100} y={40} width={470} height={340}/>
          </clipPath>
        </defs>

        {/* ── S-plane ── */}
        <rect x={10} y={10} width={560} height={380} rx={5} fill="#fafcff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={285} y={28} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">s-Plane Pole Location Map</text>

        {/* Background regions */}
        <rect x={100} y={40} width={cx-100} height={340} fill="#27ae60" opacity="0.05"/>
        <rect x={cx}  y={40} width={570-cx} height={340} fill="#c0392b" opacity="0.05"/>

        {/* Iso-lines for decay rate (vertical = equal sigma) */}
        {[-3,-2,-1].map(sv => (
          <g key={sv}>
            <line x1={px(sv)} y1={40} x2={px(sv)} y2={380} stroke="#e8f0e8" strokeWidth="1.2" strokeDasharray="4,4"/>
            <text x={px(sv)} y={390} textAnchor="middle" fontSize={8} fill="#ccc">σ={-sv}</text>
          </g>
        ))}
        {[1].map(sv => (
          <line key={sv} x1={px(sv)} y1={40} x2={px(sv)} y2={380} stroke="#fde8e8" strokeWidth="1.2" strokeDasharray="4,4"/>
        ))}

        {/* Iso-frequency lines (horizontal) */}
        {[-3,-2,-1,1,2,3].map(jv => (
          <line key={jv} x1={100} y1={py(jv)} x2={570} y2={py(jv)} stroke="#f0f0f0" strokeWidth="1"/>
        ))}

        {/* Axes */}
        <line x1={100} y1={cy}  x2={574} y2={cy}  stroke="#bbb" strokeWidth="1.8" markerEnd="url(#pm-blk)"/>
        <line x1={cx}  y1={40}  x2={cx}  y2={385} stroke="#bbb" strokeWidth="1.8" markerEnd="url(#pm-blk)"/>
        <text x={578} y={cy+4}  fontSize={10} fill="#888">σ (Real)</text>
        <text x={cx+4} y={38}   fontSize={10} fill="#888">jω (Imag)</text>
        <text x={cx-4} y={cy+14} fontSize={9} fill="#aaa">0</text>

        {/* Stable / Unstable labels */}
        <text x={180} y={56} fontSize={11} fill="#27ae60" fontWeight="700">STABLE (LHP)</text>
        <text x={cx+30} y={56} fontSize={11} fill="#c0392b" fontWeight="700">UNSTABLE (RHP)</text>

        {/* ── Pole markers with inset curves ── */}
        {/* 1. Far left real — fast decay */}
        <text x={px(-3)} y={cy+4}  textAnchor="middle" fontSize={20} fill="#27ae60" fontWeight="700">×</text>
        <rect x={px(-3)-40} y={cy-70} width={84} height={52} rx={3} fill="white" stroke="#27ae60" strokeWidth="1"/>
        <path d={mkDecay(px(-3)-38, cy-42, '#27ae60')} fill="none" stroke="#27ae60" strokeWidth="1.5"/>
        <text x={px(-3)} y={cy-20} textAnchor="middle" fontSize={7.5} fill="#27ae60">Fast decay</text>

        {/* 2. Near origin real — slow decay */}
        <text x={px(-0.6)} y={cy+4} textAnchor="middle" fontSize={20} fill="#2E75B6" fontWeight="700">×</text>
        <rect x={px(-0.6)-42} y={cy+16} width={84} height={52} rx={3} fill="white" stroke="#2E75B6" strokeWidth="1"/>
        <path d={mkDecay(px(-0.6)-40, cy+44, '#2E75B6')} fill="none" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={px(-0.6)} y={cy+76} textAnchor="middle" fontSize={7.5} fill="#2E75B6">Slow decay</text>

        {/* 3. LHP complex — damped oscillation */}
        <text x={px(-1.2)} y={py(2)+4}  textAnchor="middle" fontSize={20} fill="#c0392b" fontWeight="700">×</text>
        <text x={px(-1.2)} y={py(-2)+4} textAnchor="middle" fontSize={20} fill="#c0392b" fontWeight="700">×</text>
        <rect x={px(-1.2)+10} y={py(2)-32} width={88} height={52} rx={3} fill="white" stroke="#c0392b" strokeWidth="1"/>
        <path d={decayOscPath05} fill="none" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={px(-1.2)+54} y={py(2)+26} textAnchor="middle" fontSize={7.5} fill="#c0392b">Damped osc.</text>

        {/* 4. LHP complex near jω — lightly damped */}
        <text x={px(-0.4)} y={py(2.5)+4}  textAnchor="middle" fontSize={20} fill="#e07b00" fontWeight="700">×</text>
        <text x={px(-0.4)} y={py(-2.5)+4} textAnchor="middle" fontSize={20} fill="#e07b00" fontWeight="700">×</text>
        <rect x={px(-0.4)+10} y={py(2.5)-12} width={88} height={52} rx={3} fill="white" stroke="#e07b00" strokeWidth="1"/>
        <path d={decayOscPath03} fill="none" stroke="#e07b00" strokeWidth="1.5"/>
        <text x={px(-0.4)+54} y={py(2.5)+46} textAnchor="middle" fontSize={7.5} fill="#e07b00">Lightly damped</text>

        {/* 5. On jω axis — sustained oscillation */}
        <text x={cx} y={py(2)+4}  textAnchor="middle" fontSize={20} fill="#9b59b6" fontWeight="700">×</text>
        <text x={cx} y={py(-2)+4} textAnchor="middle" fontSize={20} fill="#9b59b6" fontWeight="700">×</text>

        {/* 6. RHP real — exponential growth */}
        <text x={px(1.5)} y={cy+4} textAnchor="middle" fontSize={20} fill="#c0392b" fontWeight="700">×</text>
        <rect x={px(1.5)+10} y={cy-68} width={88} height={52} rx={3} fill="white" stroke="#c0392b" strokeWidth="1"/>
        <path d={mkGrow(px(1.5)+12, cy-42, '#c0392b')} fill="none" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={px(1.5)+54} y={cy-14} textAnchor="middle" fontSize={7.5} fill="#c0392b">Exponential growth</text>

        {/* 7. RHP complex — growing oscillation */}
        <text x={px(0.8)} y={py(2)+4}  textAnchor="middle" fontSize={20} fill="#c0392b" fontWeight="700">×</text>
        <text x={px(0.8)} y={py(-2)+4} textAnchor="middle" fontSize={20} fill="#c0392b" fontWeight="700">×</text>
        <rect x={px(0.8)+10} y={py(-2)+10} width={88} height={52} rx={3} fill="white" stroke="#c0392b" strokeWidth="1"/>
        <path d={growOscPath} fill="none" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={px(0.8)+54} y={py(-2)+68} textAnchor="middle" fontSize={7.5} fill="#c0392b">Growing osc.</text>

        {/* Origin pole */}
        <circle cx={cx} cy={cy} r={5} fill="none" stroke="#9b59b6" strokeWidth="1.5" strokeDasharray="3,2"/>
        <text x={cx+8} y={cy+14} fontSize={8} fill="#9b59b6">Integrator (1/s)</text>
        <text x={cx+8} y={cy+24} fontSize={8} fill="#9b59b6">→ ramp output</text>

        {/* jω axis annotations */}
        <text x={cx-8} y={py(2)+4} textAnchor="end" fontSize={8} fill="#9b59b6">Sustained</text>
        <text x={cx-8} y={py(2)+14} textAnchor="end" fontSize={8} fill="#9b59b6">oscillation</text>

        {/* ── RIGHT: Summary table ── */}
        <rect x={575} y={10} width={315} height={380} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={732} y={28} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Pole Type → Response Summary</text>

        {[
          { loc:'Negative real\n(far left)',   color:'#27ae60', resp:'Fast exponential decay\n→ fast stable settling' },
          { loc:'Negative real\n(near origin)', color:'#2E75B6', resp:'Slow exponential decay\n→ sluggish but stable' },
          { loc:'Complex LHP\n(low |σ|)',       color:'#e07b00', resp:'Lightly damped oscillation\n→ rings, slowly decays' },
          { loc:'Complex LHP\n(high |σ|)',      color:'#c0392b', resp:'Well-damped oscillation\n→ quick decay, little ring' },
          { loc:'On jω axis\n(imaginary)',       color:'#9b59b6', resp:'Sustained sinusoidal osc.\n→ marginally stable' },
          { loc:'At origin\n(integrator)',        color:'#888',    resp:'Ramp output to step input\n→ marginally stable' },
          { loc:'Positive real\n(RHP)',           color:'#c0392b', resp:'Exponential divergence\n→ UNSTABLE' },
          { loc:'Complex RHP',                    color:'#c0392b', resp:'Growing oscillation\n→ UNSTABLE (diverges)' },
        ].map(({ loc, color, resp }, i) => (
          <g key={i}>
            <rect x={583} y={36+i*42} width={299} height={40} rx={2}
              fill={i % 2 === 0 ? '#fafafa' : '#fff'} stroke={color} strokeWidth="0.8"/>
            <rect x={583} y={36+i*42} width={5} height={40} rx={1} fill={color}/>
            <text x={596} y={50+i*42} fontSize={9} fill={color} fontWeight="700">{loc.split('\n')[0]}</text>
            <text x={596} y={62+i*42} fontSize={8.5} fill="#555">{loc.split('\n')[1]}</text>
            <text x={740} y={50+i*42} fontSize={9}   fill="#333">{resp.split('\n')[0]}</text>
            <text x={740} y={62+i*42} fontSize={8.5} fill="#555">{resp.split('\n')[1]}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Every pole in the s-plane has a unique contribution to the time-domain response. Poles in the left half-plane produce decaying terms (stable); the further left, the faster the decay. Complex poles produce oscillation at frequency ωd (the imaginary part). Poles on the jω axis give sustained oscillation (marginal stability). Any pole in the right half-plane causes exponential divergence (unstable). This map is the fundamental link between frequency-domain analysis and time-domain behaviour.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main Export
// ─────────────────────────────────────────────────────────────────
export default function FirstSecondOrderResponse() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Control Systems — First &amp; Second Order System Response</h1>
      <p style={S.p}>
        First and second-order systems are the workhorses of control systems analysis. Almost every higher-order system can be approximated by, or decomposed into, combinations of first and second-order components. Understanding how these systems respond to standard test signals — step, impulse, ramp, and sinusoid — is the foundation for predicting and designing the performance of any control system. The two key parameters, time constant τ (first order) and damping ratio ζ / natural frequency ωₙ (second order), completely determine the character of every response.
      </p>

      <InfoBox title="Core Concepts at a Glance" items={[
        "First-order: G(s) = K/(τs+1) — one pole, one energy storage element, pure exponential response",
        "Second-order: G(s) = ωₙ²/(s²+2ζωₙs+ωₙ²) — two poles, natural frequency ωₙ and damping ratio ζ",
        "Time constant τ: time for first-order step response to reach 63.2% of final value; settled at 5τ",
        "Damping ratio ζ: ζ<1 underdamped (oscillatory), ζ=1 critically damped, ζ>1 overdamped",
        "Five transient metrics: delay time Td, rise time Tr, peak time Tp, overshoot %OS, settling time Ts",
        "All metrics determined by ζ and ωₙ: %OS = exp(−πζ/√(1−ζ²))×100, Ts ≈ 4/(ζωₙ), Tp = π/ωd",
        "Bode plot: first order −20 dB/decade, −3 dB at ωc=1/τ; second order −40 dB/decade, resonant peak for ζ<0.707",
        "Pole location determines response character: LHP = stable, jω-axis = marginal, RHP = unstable",
      ]}/>

      <h2 style={S.h2}>First-Order System — Step Response</h2>
      <p style={S.p}>
        A first-order system has a single energy storage element — a capacitor in an RC circuit, the thermal mass of a body, or the inertia of a damped mechanical system with no spring. Its transfer function G(s) = K/(τs+1) has one pole at s = −1/τ. The step response is a simple exponential rise characterized entirely by two numbers: the DC gain K (final value) and the time constant τ (speed).
      </p>
      <DiagramFirstOrderStep/>
      <Table2 rows={[
        ["Transfer function","G(s) = K / (τs + 1)\nK = DC gain (steady-state output per unit input)\nτ = time constant [s] = 1/|pole|"],
        ["Step response","y(t) = K(1 − e^(−t/τ))  for t ≥ 0\nStarts at 0, rises smoothly to K\nNo overshoot, no oscillation — monotonic"],
        ["Time constant τ","y(τ) = K(1 − e⁻¹) ≈ 0.632K\nPhysically: time for output to reach 63.2% of final value\nSet by system parameters: τ = RC (electrical), τ = M/b (mechanical)"],
        ["Settling time (2%)", "Ts ≈ 4τ  (within 2% of final value)\nTs ≈ 5τ  (within 1%)"],
        ["Rise time Tr","Tr ≈ 2.2τ  (10% to 90%)\nDerivation: t = −τ ln(1−y/K) → t₉₀−t₁₀ ≈ τ(ln9) ≈ 2.2τ"],
        ["Pole location","Single real pole at s = −1/τ (left half-plane → stable)\nFurther left (larger 1/τ) → faster response"],
        ["DC gain K","G(0) = K — value of G(s) as s→0 (apply Final Value Theorem)\nFor RC circuit: K=1 (unity gain)"],
        ["Initial slope","dy/dt|_{t=0} = K/τ — the initial rate of rise\nTangent at t=0 reaches K at t=τ — graphical method to find τ"],
      ]}/>

      <h2 style={S.h2}>First-Order Impulse, Ramp &amp; Frequency Response</h2>
      <p style={S.p}>
        Every LTI system's response to any input can be derived from its transfer function. Beyond the step response, the impulse response reveals the system's "memory" (it is the inverse Laplace of G(s)), the ramp response shows steady-state tracking error, and the frequency response (Bode plot) reveals how the system filters different frequencies.
      </p>
      <DiagramFirstOrderInputTypes/>
      <Table2 rows={[
        ["Impulse response","h(t) = (K/τ)e^(−t/τ) — decaying exponential from K/τ at t=0\nThis is G(s) inverse-Laplace transformed\nArea under curve = K (steady-state gain check)"],
        ["Ramp response","y(t) = K[t − τ(1−e^(−t/τ))]\nAfter transient: y → K(t − τ) — tracks ramp but lags by τ seconds\nSteady-state ramp error: e_ss = Kτ"],
        ["Frequency response","G(jω) = K / (jωτ + 1)\n|G(jω)| = K/√(1+(ωτ)²)  ∠G(jω) = −arctan(ωτ)"],
        ["Corner frequency ωc","ωc = 1/τ  [rad/s] — where magnitude is −3 dB (down to K/√2)\nBandwidth: the system passes frequencies below ωc, attenuates above"],
        ["Bode magnitude","Flat at 20·log₁₀(K) dB for ω << ωc\n−20 dB/decade slope for ω >> ωc\nSingle breakpoint at ω = ωc"],
        ["Bode phase","0° for ω << ωc,  −45° exactly at ωc,  −90° for ω >> ωc\nS-shaped transition spanning ~2 decades"],
        ["Sinusoidal steady state","y_ss(t) = K·A/√(1+(ωτ)²) · sin(ωt − arctan(ωτ))\nA = input amplitude; phase lag increases with frequency"],
        ["Type 0 system","G(s)=K/(τs+1) has no free integrator → Type 0\nFinite steady-state position error to step: e_ss = 1/(1+K) for unity feedback"],
      ]}/>

      <h2 style={S.h2}>First-Order Bode Plot</h2>
      <p style={S.p}>
        The Bode plot is the frequency-domain portrait of the first-order system. Its defining feature is a single break point at the corner frequency ωc = 1/τ, where the magnitude asymptote changes slope from 0 to −20 dB/decade and the phase passes through −45°. The bandwidth (the frequency range over which the system transmits signals with less than 3 dB attenuation) equals ωc.
      </p>
      <DiagramFirstOrderBode/>
      <Table2 rows={[
        ["Corner frequency ωc","ωc = 1/τ  [rad/s] — the only breakpoint\nAt ωc: actual magnitude is −3 dB below asymptote\nPhase is exactly −45°"],
        ["Magnitude asymptotes","Low-freq asymptote: 0 dB (constant K=1)\nHigh-freq asymptote: −20 dB/decade from ωc\nAsymptotes intersect at ωc — Bode straight-line approximation"],
        ["Phase asymptotes","Low: 0° (exact below ω = ωc/10)\nHigh: −90° (exact above ω = 10ωc)\nLinear approximation: −45°/decade between ωc/10 and 10ωc"],
        ["Effect of τ on Bode","Larger τ: pole closer to origin → ωc shifts left → narrower bandwidth\nSmaller τ: ωc shifts right → wider bandwidth, faster response"],
        ["Effect of K on Bode","Shifts magnitude plot up/down by 20·log₁₀(K) dB\nNo effect on phase"],
        ["Gain margin","First-order phase never reaches −180° → infinite gain margin\nFirst-order systems are unconditionally stable in closed loop (unity feedback)"],
        ["Phase margin","Phase margin = 180° − |phase at gain crossover|\nFor first-order: PM always > 90° — very robust"],
        ["Bandwidth ω_BW","ω_BW = ωc = 1/τ for unity gain K\nFor K≠1: ω_BW = √(K²−1)^(1/2)/τ"],
      ]}/>

      <h2 style={S.h2}>Second-Order System — Standard Form &amp; Poles</h2>
      <p style={S.p}>
        The standard second-order transfer function G(s) = ωₙ²/(s²+2ζωₙs+ωₙ²) is parameterized by just two numbers: ωₙ (natural frequency, sets speed) and ζ (damping ratio, sets shape). The denominator's roots — the closed-loop poles — can be real or complex conjugate depending on ζ, and their location in the s-plane completely determines the character of every response.
      </p>
      <DiagramSecondOrderPoles/>
      <Table2 rows={[
        ["Standard form","G(s) = ωₙ² / (s² + 2ζωₙs + ωₙ²)\nDenominator also written: (s+ζωₙ)² + ωd² where ωd=ωₙ√(1−ζ²)"],
        ["Natural frequency ωₙ","Distance from origin to pole in s-plane: ωₙ = |s|\nSets the speed of the undamped oscillation\nHigher ωₙ → faster system (all metrics scale as 1/ωₙ)"],
        ["Damping ratio ζ","ζ = cos(θ) where θ = angle from negative real axis to pole\nζ = actual damping / critical damping\nDimensionless — determines shape of response"],
        ["Characteristic equation","s² + 2ζωₙs + ωₙ² = 0\nRoots: s = −ζωₙ ± ωₙ√(ζ²−1)"],
        ["Damped natural frequency","ωd = ωₙ√(1−ζ²)  (imaginary part of poles for ζ<1)\nActual oscillation frequency in time domain"],
        ["Decay rate σ","σ = ζωₙ  (negative real part of complex poles)\nRate at which oscillations decay: e^(−σt)"],
        ["DC gain","G(0) = ωₙ²/ωₙ² = 1  (unity DC gain by construction)\nFinal value to unit step = 1 (no steady-state error)"],
        ["Pole locus","As ζ varies 0→∞ with ωₙ fixed:\nPoles sweep from ±jωₙ (ζ=0) → −ωₙ (ζ=1) → two separate real poles (ζ>1)"],
      ]}/>

      <h2 style={S.h2}>Second-Order Step Response Family</h2>
      <p style={S.p}>
        The step response of the second-order system is the most important single graph in introductory control systems. It shows the fundamental trade-off between speed and overshoot. The damping ratio ζ entirely controls this trade-off: decreasing ζ gives faster rise but more overshoot; increasing ζ eliminates overshoot at the cost of a slower, more sluggish response.
      </p>
      <DiagramSecondOrderFamily/>
      <Table2 rows={[
        ["Underdamped (ζ<1)","y(t) = 1 − e^(−ζωₙt)[cos(ωdt) + (ζωₙ/ωd)sin(ωdt)]\nOscillates and overshoots; envelope decays as e^(−ζωₙt)"],
        ["Critically damped (ζ=1)","y(t) = 1 − e^(−ωₙt)(1 + ωₙt)\nFastest monotonic response — no overshoot\nDouble pole at s = −ωₙ"],
        ["Overdamped (ζ>1)","y(t) = 1 + (ωₙ²/(s₁(s₁−s₂)))e^(s₁t) + (ωₙ²/(s₂(s₂−s₁)))e^(s₂t)\nTwo real poles; response is sum of two exponentials\nDominant pole (slower) determines most of the response"],
        ["Percent overshoot","%OS = e^(−πζ/√(1−ζ²)) × 100\nDepends only on ζ, not on ωₙ\nζ=0.3: 37%, ζ=0.5: 16.3%, ζ=0.707: 4.3%, ζ≥1: 0%"],
        ["Overshoot ↔ ζ","Given %OS spec: ζ = −ln(%OS/100) / √(π² + ln²(%OS/100))\nKey design equation: spec on OS → spec on ζ"],
        ["ωₙ scales all times","All time metrics (Tr, Tp, Ts) scale inversely with ωₙ\nDoubling ωₙ halves all times — speed vs ζ are independent specs"],
        ["Design approach","1. Spec %OS → find required ζ\n2. Spec Ts or Tp → find required ωₙ\n3. Desired poles: s = −ζωₙ ± jωd"],
        ["Dominant poles","Higher-order systems: find the two poles closest to jω axis\nThese 'dominant poles' set the approximate second-order response"],
      ]}/>

      <h2 style={S.h2}>Transient Performance Metrics</h2>
      <p style={S.p}>
        Performance specifications for a control system are almost always stated in terms of the five standard transient metrics measured from the unit step response. Every metric maps directly to a constraint on the desired closed-loop pole locations — this is the bridge from specifications to controller design.
      </p>
      <DiagramTransientMetrics/>
      <Table2 rows={[
        ["Delay time Td","Time for response to first reach 50% of final value\nTd ≈ (1 + 0.7ζ)/ωₙ  (approximate)\nNot commonly used as a primary spec — Tr more often specified"],
        ["Rise time Tr","Time to go from 10% to 90% of final value\nTr ≈ (π − β)/ωd  where β = cos⁻¹(ζ)\nTr ≈ 1.8/ωₙ  for ζ≈0.5 (rough rule)"],
        ["Peak time Tp","Time to reach the first (highest) peak\nTp = π/ωd = π/(ωₙ√(1−ζ²))\nExact formula — no approximation needed"],
        ["Percent overshoot %OS","%OS = (yₚ−y_final)/y_final × 100\n%OS = e^(−πζ/√(1−ζ²)) × 100\nTypical spec: %OS ≤ 10–20%"],
        ["Settling time Ts (2%)","Ts ≈ 4/(ζωₙ) = 4/σ  [2% criterion]\nTs ≈ 3/(ζωₙ)  [5% criterion]\nMost important spec — relates directly to closed-loop bandwidth"],
        ["Steady-state error e_ss","e_ss = lim(t→∞)[r(t)−y(t)]  for step: e_ss = 0 for Type≥1\nFor Type 0 with unity feedback: e_ss = 1/(1+Kp)"],
        ["Metric independence","%OS and ζ are linked (ζ ↔ OS trade-off)\nTs and ζωₙ are linked  |  Tp and ωd are linked\nωₙ and ζ are independent — set separately"],
        ["Spec-to-pole mapping","%OS spec → ζ → Im(pole)/Re(pole) = ωd/σ (slope in s-plane)\nTs spec → σ = ζωₙ ≥ 4/Ts (min decay rate)\nDesired poles lie at intersection of angle line and vertical line"],
      ]}/>

      <h2 style={S.h2}>Second-Order Bode Plot</h2>
      <p style={S.p}>
        The second-order Bode plot adds new features absent from the first-order case: a −40 dB/decade high-frequency roll-off (twice as fast) and, for underdamped systems (ζ &lt; 0.707), a resonant peak in the magnitude that can cause large frequency-domain distortions. The phase transitions from 0° to −180° across two decades, always passing through exactly −90° at ωₙ.
      </p>
      <DiagramSecondOrderBode/>
      <Table2 rows={[
        ["Magnitude roll-off","−40 dB/decade for ω >> ωₙ  (two poles → twice the slope)\nAsymptote break at ω = ωₙ"],
        ["Resonant frequency ωᵣ","ωᵣ = ωₙ√(1−2ζ²)  for ζ < 1/√2 ≈ 0.707\nFor ζ ≥ 0.707: no resonant peak (monotonically decreasing)"],
        ["Resonant peak Mᵣ","Mᵣ = 1/(2ζ√(1−ζ²))  at ω = ωᵣ\nMᵣ in dB = 20 log₁₀(Mᵣ)\nζ=0.1: 14 dB peak;  ζ=0.5: 1.2 dB;  ζ=0.707: 0 dB"],
        ["Bandwidth ω_BW","ω_BW = ωₙ√(1−2ζ²+√(4ζ⁴−4ζ²+2))\nFor ζ=0.707: ω_BW = ωₙ  |  For ζ→0: ω_BW → ωₙ√2"],
        ["Phase at ωₙ","Phase = −90° exactly at ω=ωₙ for all ζ (regardless of damping)\nThis is because denominator angle = −90° when Im/Re = ωₙ²/0"],
        ["Phase range","0° (very low ω) → −180° (very high ω)\nAll 180° of phase change occurs — second-order lag"],
        ["Gain margin (second order)","Phase crosses −180° only at ω→∞ for ζ>0 → infinite gain margin\nFor ζ=0: phase reaches −180° at finite frequency → zero gain margin"],
        ["ωₙ and ζ from Bode","Identify ωₙ as −90° crossing or resonant peak frequency\nEstimate ζ from peak height: Mᵣ_dB → ζ = 1/(2·10^(Mᵣ_dB/20))"],
      ]}/>

      <h2 style={S.h2}>Pole Location → Time-Domain Behaviour</h2>
      <p style={S.p}>
        The most important skill in control systems is reading a pole-zero plot and immediately knowing the time-domain behaviour it implies. Every pole contributes one exponential (or oscillatory-exponential) term to the response; the pole's position in the s-plane dictates exactly how that term behaves.
      </p>
      <DiagramPoleMap/>
      <Table2 rows={[
        ["Real negative pole at s=−a","Contributes: C·e^(−at)  (decaying exponential)\nSpeed: faster for larger a  |  Time constant: τ=1/a\nAll physical RC, thermal, fluid systems have this form"],
        ["Complex conjugate pair s=−σ±jωd","Contributes: e^(−σt)[A cos(ωdt) + B sin(ωdt)]\nDecay rate: σ = ζωₙ  |  Oscillation frequency: ωd\nDamping: ζ = σ/ωₙ = σ/√(σ²+ωd²) = cos(angle from −jω axis)"],
        ["Purely imaginary pair s=±jωd","Contributes: A cos(ωdt) — sustained oscillation forever\nMarginally stable — used in oscillator design\nIn closed loop: tiny gain increase causes RHP poles"],
        ["Pole at origin s=0","Contributes: constant — integrator\nPure ramp output to step input\nAdds system Type number by 1 → eliminates steady-state error"],
        ["Real positive pole s=+a","Contributes: C·e^(+at) — diverges to infinity\nSystem is UNSTABLE — must be stabilized by feedback"],
        ["Complex RHP pair s=σ±jωd (σ>0)","Contributes: e^(+σt)·oscillation — diverging oscillation\nSystem is UNSTABLE and oscillating — requires control"],
        ["Dominant poles","Poles closest to jω axis have largest time constants (slowest decay)\nDominate the long-time behaviour — can approximate higher-order system\nRule: dominant if at least 5× closer to jω axis than other poles"],
        ["Pole-zero cancellation","If a zero is placed very near a pole, their effects cancel\nThe cancelled mode is unobservable/uncontrollable\nDangerous if the cancelled pole is in the RHP"],
      ]}/>

      <h2 style={S.h2}>Comparing First and Second Order Systems</h2>
      <Table2 rows={[
        ["Transfer function","1st: K/(τs+1)  — 1 parameter\n2nd: ωₙ²/(s²+2ζωₙs+ωₙ²) — 2 parameters"],
        ["Number of poles","1st: one real pole at s=−1/τ\n2nd: two poles (real or complex conjugate)"],
        ["Step response shape","1st: monotonic exponential — no overshoot\n2nd: can oscillate and overshoot (if ζ<1)"],
        ["Bode roll-off","1st: −20 dB/decade above ωc\n2nd: −40 dB/decade above ωₙ"],
        ["Phase range","1st: 0° to −90° (90° total)\n2nd: 0° to −180° (180° total)"],
        ["Resonant behaviour","1st: no resonant peak (monotonic |G(jω)|)\n2nd: resonant peak for ζ < 0.707 at ω = ωₙ√(1−2ζ²)"],
        ["Stability (in isolation)","1st: always stable if τ>0 (pole always in LHP)\n2nd: always stable if ζ>0 and ωₙ>0 (both coefficient signs same)"],
        ["Approximation","Higher-order systems → dominant 2nd-order pair often captures main behaviour\nFast poles can be neglected if 5× further left than dominant poles"],
      ]}/>

      <h2 style={S.h2}>System Identification from Step Response</h2>
      <Table2 rows={[
        ["First-order from step","1. Read K = final value\n2. Find t where y = 0.632K → that is τ\n3. Or: tangent at t=0 intersects y=K at t=τ"],
        ["Second-order underdamped","1. Read K = final value,  yₚ = peak value\n2. %OS = (yₚ−K)/K × 100  → compute ζ\n3. Read Tp → ωd = π/Tp  → ωₙ = ωd/√(1−ζ²)"],
        ["Second-order overdamped","Approximate as dominant first-order: τ_dom ≈ −1/s₁\nOr use two-point method on rising slope"],
        ["From Bode experiment","Measure |G(jω)| and phase at many frequencies\nFind corner frequency (−3 dB) → ωc = 1/τ for 1st order\nFind resonant peak → get ζ; find phase crossover → get ωₙ"],
        ["Ziegler-Nichols process reaction","Step test: find S-curve delay L and time constant T\nSimple approximation for PID tuning: Kp = T/L etc."],
        ["Frequency sweep","Apply sinusoid at each frequency; measure amplitude ratio and phase lag\nSafer than step for fragile plants; required for Nyquist plot"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "1st order step: y(t) = K(1−e^(−t/τ)),  τ = time constant,  K = DC gain",
        "1st order impulse: h(t) = (K/τ)e^(−t/τ),  bandwidth ωBW = 1/τ",
        "2nd order standard: G(s) = ωₙ²/(s²+2ζωₙs+ωₙ²),  poles at −ζωₙ ± jωd",
        "Damped frequency: ωd = ωₙ√(1−ζ²)  |  Decay rate: σ = ζωₙ",
        "%OS = exp(−πζ/√(1−ζ²))×100  |  Tp = π/ωd  |  Ts ≈ 4/(ζωₙ)",
        "Resonant peak: Mᵣ = 1/(2ζ√(1−ζ²)) at ωᵣ = ωₙ√(1−2ζ²)  [only if ζ < 0.707]",
        "Design from specs: ζ from %OS, then ωₙ from Ts → desired poles: −ζωₙ ± jωd",
        "Bode: 1st order −20 dB/dec, −45° at ωc;  2nd order −40 dB/dec, −90° at ωₙ",
      ]}/>
    </div>
  );
}